const MAX_MOUNTAIN_RANGES = 6;
const PIXEL_ART_ENABLED = false; // set false to compare against the smooth landscape
const PIXEL_SCALE = 4; // higher number = chunkier pixels
const PIXEL_VERTEX_GRID = 1; // higher number = more pixelated
const SKY_BAND_HEIGHT = 2; // height of the sky bands, higher number = more sky bands
const CLOUD_SPAWN_CHANCE = 0.65; // 0 = never, 1 = every generation
const CLOUD_MIN_COUNT = 1;
const CLOUD_MAX_COUNT = 10;
const FOG_CHANCE = 0.4; // probability fog appears each generation
const RAIN_CHANCE = 0.28; // probability rain appears each generation
const FOREGROUND_LAND_ROWS = 2;
const LAND_LAKE_CHANCE = 0.16;
const LAND_RIVER_CHANCE = 0.12;
const LAND_LANDMARK_CHANCE = 0.96;
const LAND_FOREST_CHANCE = 0.74;
const LAND_ROCK_FIELD_CHANCE = 0.38;
const LAND_DENSE_FOREST_CHANCE = 0.15;
const LAND_TOWN_CHANCE = 0.05;
const LAND_CASTLE_CHANCE = 0.05;
const ARCH_STYLES = ['european', 'greek', 'arabic', 'east-asian', 'indian'];
const PRICE_SMOOTHING_BLEND = 0.68; // blend factor for the smoothed prices, higher number = more smoothing
const PRICE_SMOOTHING_KERNEL = [1, 2, 3, 2, 1];
const PRICE_SMOOTHING_PASSES = 2; // number of passes to smooth the prices, higher number = more smoothing

const getClosePrices = (chartData) =>
    (chartData?.values || [])
        .map((value) => Number(value.close))
        .filter(Number.isFinite);

const getClampedPrice = (prices, index) =>
    prices[Math.max(0, Math.min(index, prices.length - 1))];

const snapToPixel = (value, grid = PIXEL_VERTEX_GRID) =>
    PIXEL_ART_ENABLED ? Math.round(value / grid) * grid : value;

const getRenderScale = () => (PIXEL_ART_ENABLED ? PIXEL_SCALE : 1);

const smoothPrices = (prices) => {
    if (prices.length < PRICE_SMOOTHING_KERNEL.length) {
        return prices;
    }

    let smoothedPrices = [...prices];
    const weightTotal = PRICE_SMOOTHING_KERNEL.reduce(
        (total, weight) => total + weight,
        0
    );
    const kernelCenter = Math.floor(PRICE_SMOOTHING_KERNEL.length / 2);

    for (let pass = 0; pass < PRICE_SMOOTHING_PASSES; pass += 1) {
        const currentPrices = smoothedPrices;
        smoothedPrices = currentPrices.map((price, index) => {
            if (index === 0 || index === currentPrices.length - 1) {
                return price;
            }

            return (
                PRICE_SMOOTHING_KERNEL.reduce((total, weight, kernelIndex) => {
                    const priceIndex = index + kernelIndex - kernelCenter;
                    return (
                        total +
                        getClampedPrice(currentPrices, priceIndex) * weight
                    );
                }, 0) / weightTotal
            );
        });
    }

    return prices.map((price, index) => {
        if (index === 0 || index === prices.length - 1) {
            return price;
        }

        return (
            price * (1 - PRICE_SMOOTHING_BLEND) +
            smoothedPrices[index] * PRICE_SMOOTHING_BLEND
        );
    });
};

export const mountainRange = (p, chartData) => {
    let isNight;
    let topColor;
    let bottomColor;
    let sun;
    let stars = null;
    let clouds = [];
    let mountainRanges = [];
    let fogParams = null;
    let rainParams = null;

    class Shape {
        constructor(x, y, width, height, strokeColor, fillColor) {
            this.x = x;
            this.y = y;
            this.width = width;
            this.height = height;
            this.strokeColor = strokeColor;
            this.fillColor = fillColor;
            this.strokeWeight = 1;
        }

        getLeft() {
            return this.x;
        }

        getRight() {
            return this.x + this.width;
        }

        getBottom() {
            return this.y;
        }

        getTop() {
            return this.y + this.height;
        }

        contains(x, y) {
            return (
                x >= this.x &&
                x <= this.x + this.width &&
                y >= this.y &&
                y <= this.y + this.height
            );
        }
    }

    const getMountainFill = (zIndex, numMountains) => {
        const sat = p.map(zIndex, 0, numMountains, 0, p.saturation(topColor));
        const bright = p.map(
            zIndex,
            0,
            numMountains,
            0,
            p.saturation(topColor)
        );

        return p.color(p.hue(topColor), sat, bright);
    };

    const getFlatLandFill = (zIndex) => {
        const depth = p.map(
            zIndex,
            0,
            p.max(FOREGROUND_LAND_ROWS - 1, 1),
            0,
            1
        );
        const hue =
            (p.hue(topColor) + p.random(isNight ? -6 : 8, isNight ? 6 : 32)) %
            255;
        const saturation = p.min(
            p.saturation(topColor) * p.lerp(0.72, 0.58, depth) +
                p.random(8, 22),
            255
        );
        const brightness = p.min(
            p.max(
                p.brightness(bottomColor) * p.lerp(0.38, 0.5, depth),
                isNight ? p.lerp(34, 48, depth) : p.lerp(58, 88, depth)
            ),
            165
        );

        return p.color(hue < 0 ? hue + 255 : hue, saturation, brightness);
    };

    const getRandomDayBGColor = () => {
        p.colorMode(p.HSB, 255);
        return p.color(p.random(0, 255), 115, 150);
    };

    const getRandomNightBGColor = () => {
        p.colorMode(p.HSB, 255);
        return p.color(
            p.random(150, 215),
            p.random(155, 205),
            p.random(28, 58)
        );
    };

    const drawBackground = (top, bottom) => {
        if (!PIXEL_ART_ENABLED) {
            const ctx = p.drawingContext;
            const gradient = ctx.createLinearGradient(0, 0, 0, p.width);
            gradient.addColorStop(0, top.toString());
            gradient.addColorStop(0.4, bottom.toString());

            const oldFillStyle = ctx.fillStyle;
            ctx.fillStyle = gradient;
            ctx.fillRect(0, 0, p.width, p.height);
            ctx.fillStyle = oldFillStyle;
            return;
        }

        p.noStroke();

        for (let y = 0; y < p.height; y += SKY_BAND_HEIGHT) {
            const amount = p.min(y / (p.height * 0.4), 1);
            p.fill(p.lerpColor(top, bottom, amount));
            p.rect(0, y, p.width, SKY_BAND_HEIGHT);
        }
    };

    const pixelVertex = (x, y) => {
        p.vertex(snapToPixel(x), snapToPixel(y));
    };

    const splineVertex = (x, y) => {
        const pixelX = snapToPixel(x);
        const pixelY = snapToPixel(y);

        if (typeof p.splineVertex === 'function') {
            p.splineVertex(pixelX, pixelY);
            return;
        }

        p.vertex(pixelX, pixelY);
    };

    // Shared terrain detail pass — adds directional lighting, geological strata,
    // and slope-based cliff shading to any mountain whose outline is described by getYAtX.
    const drawMountainFaceTerrain = (getYAtX, topY, noiseSeed) => {
        const ctx = p.drawingContext;
        ctx.save();

        // Clip to the mountain silhouette so nothing bleeds outside it
        ctx.beginPath();
        ctx.moveTo(-20, p.height);
        for (let x = -20; x <= p.width + 20; x += 4) {
            ctx.lineTo(x, getYAtX(x));
        }
        ctx.lineTo(p.width + 20, p.height);
        ctx.closePath();
        ctx.clip();

        const mountainH = p.height - topY;

        // Directional light gradient — upper-left source
        const lightGrad = ctx.createLinearGradient(0, topY, p.width, p.height);
        lightGrad.addColorStop(0, 'rgba(255,255,255,0.07)');
        lightGrad.addColorStop(0.38, 'rgba(255,255,255,0.00)');
        lightGrad.addColorStop(0.65, 'rgba(0,0,0,0.04)');
        lightGrad.addColorStop(1, 'rgba(0,0,0,0.20)');
        ctx.fillStyle = lightGrad;
        ctx.fillRect(-20, topY - 10, p.width + 40, mountainH + 20);

        // Geological strata — wavy horizontal bands suggesting rock layers
        const nLayers = 11;
        for (let i = 1; i < nLayers; i++) {
            const t = i / nLayers;
            const baseY = topY + mountainH * t;
            const seed = noiseSeed + i * 173;
            const bandH = mountainH * 0.014;

            ctx.beginPath();
            let first = true;
            for (let x = -20; x <= p.width + 20; x += 5) {
                const slow = (p.noise(x * 0.0035 + seed) - 0.5) * 2;
                const fast = (p.noise(x * 0.013 + seed, 8) - 0.5) * 2;
                const y =
                    baseY + (slow * 0.65 + fast * 0.35) * mountainH * 0.04;
                if (first) {
                    ctx.moveTo(x, y);
                    first = false;
                } else ctx.lineTo(x, y);
            }
            for (let x = p.width + 20; x >= -20; x -= 5) {
                const slow = (p.noise(x * 0.0035 + seed) - 0.5) * 2;
                const fast = (p.noise(x * 0.013 + seed, 8) - 0.5) * 2;
                ctx.lineTo(
                    x,
                    baseY +
                        bandH +
                        (slow * 0.65 + fast * 0.35) * mountainH * 0.04
                );
            }
            ctx.closePath();
            ctx.fillStyle =
                i % 3 === 0 ? 'rgba(0,0,0,0.09)' : 'rgba(0,0,0,0.04)';
            ctx.fill();
        }

        // Slope shading — steep right-facing slopes darken (cliff shadow),
        // steep left-facing slopes receive a faint highlight
        for (let x = 0; x <= p.width; x += 4) {
            const slope = (getYAtX(x + 5) - getYAtX(x - 5)) / 10;
            const colTopY = getYAtX(x);
            const colH = p.height - colTopY;
            if (colH <= 0) continue;

            if (slope > 0.55) {
                const alpha = Math.min((slope - 0.55) * 0.22, 0.28);
                ctx.fillStyle = `rgba(0,0,0,${alpha.toFixed(3)})`;
                ctx.fillRect(x, colTopY, 4, colH);
            } else if (slope < -0.55) {
                const alpha = Math.min((-slope - 0.55) * 0.09, 0.11);
                ctx.fillStyle = `rgba(255,255,255,${alpha.toFixed(3)})`;
                ctx.fillRect(x, colTopY, 4, colH);
            }
        }

        ctx.restore();
    };

    class Sun extends Shape {
        constructor() {
            const size = 50 + p.width * p.random(0.1, 0.3);
            const xLoc = p.width * p.random();
            const yLoc = size * p.random(-0.2, 0.5);
            super(xLoc, yLoc, size, size);

            this.fillColor = p.color(
                p.hue(topColor),
                p.saturation(topColor) * 0.9,
                p.brightness(topColor) * 1.6
            );
        }

        draw() {
            p.noStroke();
            p.fill(this.fillColor);
            p.ellipse(
                snapToPixel(this.x),
                snapToPixel(this.y),
                snapToPixel(this.width),
                snapToPixel(this.height)
            );
        }
    }

    class Stars {
        constructor() {
            this.points = this.generate();
        }

        generate() {
            const count = Math.floor(p.random(120, 300));
            return Array.from({ length: count }, () => ({
                x: p.random(p.width),
                y: p.random(p.height * 0.62),
                size: p.random(0.6, 2.8),
                hue: p.random() > 0.65 ? p.random(150, 200) : p.random(20, 50),
                sat: p.random(0, 20),
                bright: p.random(185, 255),
                alpha: p.random(110, 235),
            }));
        }

        draw() {
            p.noStroke();
            this.points.forEach(({ x, y, size, hue, sat, bright, alpha }) => {
                p.fill(hue, sat, bright, alpha);
                p.circle(x, y, size);
            });
        }
    }

    class Moon extends Shape {
        constructor() {
            const size = 40 + p.width * p.random(0.04, 0.12);
            const xLoc = p.width * p.random(0.1, 0.9);
            const yLoc = size * p.random(0.3, 1.0);
            super(xLoc, yLoc, size, size);

            const phases = ['full', 'gibbous', 'quarter', 'crescent'];
            this.phase = p.random(phases);
            this.direction = p.random() > 0.5 ? 1 : -1;

            this.moonColor = p.color(38, p.random(15, 40), p.random(200, 235));

            const skyAmount = p.constrain(yLoc / (p.height * 0.4), 0, 1);
            const skyAtMoon = p.lerpColor(topColor, bottomColor, skyAmount);
            this.shadowColor = p.color(
                p.hue(skyAtMoon),
                p.min(p.saturation(skyAtMoon) * 1.15, 255),
                p.brightness(skyAtMoon) * 0.82
            );

            // Pre-compute the blended crater colors so palette quantization
            // preserves enough contrast for craters to remain visible
            const mr = p.red(this.moonColor);
            const mg = p.green(this.moonColor);
            const mb = p.blue(this.moonColor);
            this.craterFloorColor = p.color(mr * 0.87, mg * 0.87, mb * 0.87);
            this.craterRimColor = p.color(
                Math.min(mr * 0.72 + 255 * 0.28, 255),
                Math.min(mg * 0.72 + 252 * 0.28, 255),
                Math.min(mb * 0.72 + 230 * 0.28, 255)
            );

            this.craters = this.generateCraters();
        }

        generateCraters() {
            const r = this.width / 2;
            const count = Math.floor(p.random(50, 75));
            const result = [];

            for (let i = 0; i < count; i += 1) {
                for (let attempt = 0; attempt < 20; attempt += 1) {
                    const angle = p.random(Math.PI * 2);
                    const dist = p.random(0, r * 0.8);
                    const ox = Math.cos(angle) * dist;
                    const oy = Math.sin(angle) * dist;
                    const sizeRoll = p.random();
                    const cr =
                        r *
                        (sizeRoll < 0.2
                            ? p.random(0.1, 0.17)
                            : p.random(0.03, 0.1));

                    if (Math.sqrt(ox * ox + oy * oy) + cr > r * 0.9) continue;

                    const overlaps = result.some((c) => {
                        const dx = ox - c.ox;
                        const dy = oy - c.oy;
                        return Math.sqrt(dx * dx + dy * dy) < (c.cr + cr) * 1.4;
                    });

                    if (!overlaps) {
                        result.push({ ox, oy, cr, depth: p.random(0.5, 1.0) });
                        break;
                    }
                }
            }

            return result;
        }

        draw() {
            const cx = this.x;
            const cy = this.y;
            const r = this.width / 2;
            const dir = this.direction;

            // Atmospheric glow: smooth radial gradient, no visible bands
            const ctx = p.drawingContext;
            const glowGrad = ctx.createRadialGradient(
                cx,
                cy,
                r * 0.85,
                cx,
                cy,
                r * 3.2
            );
            glowGrad.addColorStop(0, 'rgba(255,248,220,0.07)');
            glowGrad.addColorStop(0.3, 'rgba(255,245,210,0.03)');
            glowGrad.addColorStop(1, 'rgba(255,245,210,0)');
            const oldGlowFill = ctx.fillStyle;
            ctx.fillStyle = glowGrad;
            ctx.fillRect(cx - r * 3.5, cy - r * 3.5, r * 7, r * 7);
            ctx.fillStyle = oldGlowFill;
            ctx.save();

            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.clip();

            const mr = Math.round(p.red(this.moonColor));
            const mg = Math.round(p.green(this.moonColor));
            const mb = Math.round(p.blue(this.moonColor));
            const moonFill = `rgb(${mr},${mg},${mb})`;

            const sr = Math.round(p.red(this.shadowColor));
            const sg = Math.round(p.green(this.shadowColor));
            const sb = Math.round(p.blue(this.shadowColor));
            const shadowFill = `rgba(${sr},${sg},${sb},0.93)`;

            // 1. Base moon color
            ctx.fillStyle = moonFill;
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

            // 2. Sphere shading — radial gradient gives a 3D feel
            const lightX = cx + dir * r * 0.28;
            const lightY = cy - r * 0.18;
            const sphereGrad = ctx.createRadialGradient(
                lightX,
                lightY,
                r * 0.05,
                cx,
                cy,
                r * 1.02
            );
            sphereGrad.addColorStop(0, 'rgba(255,250,235,0.07)');
            sphereGrad.addColorStop(0.65, 'rgba(0,0,0,0.0)');
            sphereGrad.addColorStop(1, 'rgba(0,0,0,0.32)');
            ctx.fillStyle = sphereGrad;
            ctx.fillRect(cx - r, cy - r, r * 2, r * 2);

            // 3. Craters — floor + shadowed inner rim + bright outer rim highlight
            const lightAngle = dir > 0 ? 0 : Math.PI;
            const shadowAngle = dir > 0 ? Math.PI : 0;

            this.craters.forEach(({ ox, oy, cr, depth }) => {
                const crx = cx + ox;
                const cry = cy + oy;

                // Crater floor
                ctx.fillStyle = `rgba(0,0,0,${0.13 * depth})`;
                ctx.beginPath();
                ctx.arc(crx, cry, cr, 0, Math.PI * 2);
                ctx.fill();

                // Inner shadow rim (on the side away from the light)
                ctx.strokeStyle = `rgba(0,0,0,${0.22 * depth})`;
                ctx.lineWidth = cr * 0.4;
                ctx.beginPath();
                ctx.arc(
                    crx,
                    cry,
                    cr * 0.7,
                    shadowAngle - 0.85,
                    shadowAngle + 0.85
                );
                ctx.stroke();

                // Bright rim highlight (on the side toward the light)
                ctx.strokeStyle = `rgba(255,252,230,${0.28 * depth})`;
                ctx.lineWidth = cr * 0.26;
                ctx.beginPath();
                ctx.arc(
                    crx,
                    cry,
                    cr * 0.9,
                    lightAngle - 0.75,
                    lightAngle + 0.75
                );
                ctx.stroke();
            });

            // 4. Phase shadow — clipped to the dark region so texture shows through on lit side
            if (this.phase !== 'full') {
                ctx.save();

                if (this.phase === 'crescent') {
                    // Shadow circle clips to the large dark region
                    ctx.beginPath();
                    ctx.arc(cx - dir * r * 0.72, cy, r * 1.08, 0, Math.PI * 2);
                    ctx.clip();
                } else if (this.phase === 'gibbous') {
                    // Clip to (moon disc) minus (lit circle) using even-odd rule
                    ctx.beginPath();
                    ctx.arc(cx, cy, r, 0, Math.PI * 2);
                    ctx.arc(
                        cx + dir * r * 0.28,
                        cy,
                        r * 0.94,
                        0,
                        Math.PI * 2,
                        true
                    );
                    ctx.clip('evenodd');
                } else if (this.phase === 'quarter') {
                    ctx.beginPath();
                    ctx.rect(dir > 0 ? cx - r : cx, cy - r, r, r * 2);
                    ctx.clip();
                }

                ctx.fillStyle = shadowFill;
                ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
                ctx.restore();
            }

            ctx.restore();
        }
    }

    class Cloud extends Shape {
        constructor() {
            const cloudWidth = p.width * p.random(0.14, 0.34);
            const cloudHeight = p.height * p.random(0.05, 0.1);
            const xLoc = p.random(-cloudWidth * 0.25, p.width - cloudWidth);
            const yLoc = p.height * p.random(0.05, 0.32);
            super(xLoc, yLoc, cloudWidth, cloudHeight);

            const cloudHue = p.hue(bottomColor);
            this.outlineColor = p.color(
                cloudHue,
                p.saturation(bottomColor) * 0.38,
                p.brightness(bottomColor) * 0.72,
                p.random(75, 125)
            );
            this.fillColor = p.color(
                cloudHue,
                p.saturation(bottomColor) * 0.08,
                p.min(p.brightness(bottomColor) * 1.45, 255),
                p.random(115, 175)
            );
            this.foreground = p.random() > 0.5;
            this.lobes = this.generateLobes();
            this.dots = this.createDots();
        }

        generateLobes() {
            const lobes = [];
            const w = this.width;
            const h = this.height;

            // Wide flat belly — anchors the bottom of the cloud
            lobes.push({
                cx: w * p.random(0.4, 0.6),
                cy: h * p.random(0.65, 0.8),
                rx: w * p.random(0.42, 0.5),
                ry: h * p.random(0.28, 0.38),
            });

            // 3-6 organic puff blobs clustered in the upper portion
            const puffCount = Math.floor(p.random(3, 7));
            for (let i = 0; i < puffCount; i += 1) {
                const puffR = h * p.random(0.32, 0.58);
                lobes.push({
                    cx: p.random(w * 0.08, w * 0.92),
                    cy: h * p.random(0.3, 0.6),
                    rx: puffR * p.random(0.85, 1.4),
                    ry: puffR,
                });
            }

            return lobes;
        }

        isInCloud(x, y) {
            return this.lobes.some((lobe) => {
                const dx = (x - lobe.cx) / lobe.rx;
                const dy = (y - lobe.cy) / lobe.ry;
                return dx * dx + dy * dy < 1;
            });
        }

        createDots() {
            const dots = [];
            const dotBaseSize = this.height * p.random(0.16, 0.24);
            const stepX = dotBaseSize * p.random(0.44, 0.58);
            const stepY = dotBaseSize * p.random(0.4, 0.54);
            const columns = Math.ceil(this.width / stepX) + 1;
            const rows = Math.ceil(this.height / stepY) + 1;

            for (let row = 0; row < rows; row += 1) {
                for (let col = 0; col < columns; col += 1) {
                    const x =
                        col * stepX + p.random(-stepX * 0.38, stepX * 0.38);
                    const y =
                        row * stepY + p.random(-stepY * 0.48, stepY * 0.48);

                    if (!this.isInCloud(x, y) || p.random() < 0.04) {
                        continue;
                    }

                    // Use the deepest lobe overlap for edge falloff
                    const maxDepth = this.lobes.reduce((best, lobe) => {
                        const dx = (x - lobe.cx) / lobe.rx;
                        const dy = (y - lobe.cy) / lobe.ry;
                        const d = Math.sqrt(dx * dx + dy * dy);
                        return d < 1 ? Math.max(best, 1 - d) : best;
                    }, 0);

                    const edgeFalloff = p.constrain(
                        p.map(maxDepth, 0, 0.25, 0.72, 1),
                        0.72,
                        1
                    );

                    dots.push({
                        x,
                        y,
                        size: dotBaseSize * p.random(0.88, 1.45) * edgeFalloff,
                    });
                }
            }

            return dots;
        }

        drawDot(dot, mode) {
            const x = snapToPixel(this.x + dot.x);
            const y = snapToPixel(this.y + dot.y);
            const size = snapToPixel(dot.size);

            if (PIXEL_ART_ENABLED) {
                const rectSize = p.max(1, size);
                if (mode === 'outline') {
                    p.noFill();
                    p.stroke(this.outlineColor);
                    p.strokeWeight(1);
                } else {
                    p.noStroke();
                    p.fill(this.fillColor);
                }

                p.rect(
                    snapToPixel(x - rectSize / 2),
                    snapToPixel(y - rectSize / 2),
                    rectSize,
                    rectSize
                );
                return;
            }

            p.noStroke();
            if (mode === 'outline') {
                p.fill(this.outlineColor);
                p.circle(x, y, size * 1.55);
            } else {
                p.fill(this.fillColor);
                p.circle(x, y, size);
            }
        }

        draw() {
            this.dots.forEach((dot) => this.drawDot(dot, 'outline'));
            this.dots.forEach((dot) => this.drawDot(dot, 'fill'));
        }
    }

    class PriceMountainRange extends Shape {
        constructor(zIndex, numMountains, prices) {
            const maxMountainHeight = p.height * 0.72;
            super(0, p.height - maxMountainHeight, p.width, maxMountainHeight);

            this.fillColor = getMountainFill(zIndex, numMountains);
            this.rawPrices = prices.length > 1 ? prices : [0, 1];
            this.prices = smoothPrices(this.rawPrices);
            this.minPrice = Math.min(...this.prices);
            this.maxPrice = Math.max(...this.prices);
            this.priceRange = this.maxPrice - this.minPrice || 1;
            this.peakY = Math.min(
                ...this.prices.map((price) => this.getPriceY(price))
            );
            this.peakHeight = p.height - this.peakY;
            this.hasSnow = p.random() > 0.8;
            this.snowThreshold = p.random(0.35, 0.65);
            this.snowNoiseSeed = p.random(1000);
        }

        getPriceY(price) {
            const pricePosition = (price - this.minPrice) / this.priceRange;
            return snapToPixel(
                p.lerp(p.height * 0.72, p.height * 0.14, pricePosition)
            );
        }

        getYAtX(x) {
            const lastIndex = this.prices.length - 1;
            const priceIndex = p.constrain(
                p.map(x, 0, p.width, 0, lastIndex),
                0,
                lastIndex
            );
            const leftIndex = Math.floor(priceIndex);
            const rightIndex = Math.min(leftIndex + 1, lastIndex);
            const amount = priceIndex - leftIndex;

            return p.lerp(
                this.getPriceY(this.prices[leftIndex]),
                this.getPriceY(this.prices[rightIndex]),
                amount
            );
        }

        drawSnowCap() {
            if (!this.hasSnow) return;

            const ctx = p.drawingContext;
            const snowLineY = this.getPriceY(
                this.maxPrice - this.priceRange * this.snowThreshold
            );
            const noiseAmt = p.height * 0.11;

            const getEdgeY = (x) => {
                const slow = p.map(
                    p.noise(x * 0.003 + this.snowNoiseSeed, 0),
                    0,
                    1,
                    -1,
                    1
                );
                const fast = p.map(
                    p.noise(x * 0.014 + this.snowNoiseSeed, 50),
                    0,
                    1,
                    -1,
                    1
                );
                return (
                    snowLineY + slow * noiseAmt * 0.65 + fast * noiseAmt * 0.35
                );
            };

            const grad = ctx.createLinearGradient(
                0,
                this.peakY - 4,
                0,
                snowLineY + noiseAmt
            );
            if (isNight) {
                grad.addColorStop(0, 'rgba(192,208,236,0.9)');
                grad.addColorStop(0.5, 'rgba(192,208,236,0.5)');
                grad.addColorStop(1, 'rgba(192,208,236,0)');
            } else {
                grad.addColorStop(0, 'rgba(236,243,255,0.94)');
                grad.addColorStop(0.5, 'rgba(236,243,255,0.58)');
                grad.addColorStop(1, 'rgba(236,243,255,0)');
            }

            // Collect contiguous patches where the ridge rises above the noisy snow edge
            const patches = [];
            let current = null;
            for (let x = -20; x <= p.width + 20; x += 2) {
                const ridgeY = this.getYAtX(x);
                const edgeY = getEdgeY(x);
                if (ridgeY <= edgeY) {
                    if (!current) current = [];
                    current.push({ x, ridgeY, edgeY });
                } else if (current) {
                    patches.push(current);
                    current = null;
                }
            }
            if (current) patches.push(current);

            ctx.save();
            ctx.fillStyle = grad;

            // For each patch: trace ridge left→right along top, then noisy edge right→left along bottom
            patches.forEach((pts) => {
                ctx.beginPath();
                ctx.moveTo(pts[0].x, pts[0].edgeY);
                pts.forEach((pt) => ctx.lineTo(pt.x, pt.ridgeY));
                for (let i = pts.length - 1; i >= 0; i--) {
                    ctx.lineTo(pts[i].x, pts[i].edgeY);
                }
                ctx.closePath();
                ctx.fill();
            });

            ctx.restore();
        }

        draw() {
            p.fill(this.fillColor);
            p.noStroke();
            p.beginShape();
            pixelVertex(-20, p.height);

            const firstY = this.getPriceY(this.prices[0]);
            splineVertex(-20, firstY);
            splineVertex(0, firstY);

            this.prices.forEach((price, index) => {
                const x = p.map(index, 0, this.prices.length - 1, 0, p.width);
                splineVertex(x, this.getPriceY(price));
            });

            const lastY = this.getPriceY(this.prices[this.prices.length - 1]);
            splineVertex(p.width, lastY);
            splineVertex(p.width + 20, lastY);
            pixelVertex(p.width + 20, p.height);
            p.endShape(p.CLOSE);
            drawMountainFaceTerrain(
                (x) => this.getYAtX(x),
                this.peakY,
                this.snowNoiseSeed
            );
            this.drawSnowCap();
        }
    }

    class FlatLandRange extends Shape {
        constructor(
            zIndex,
            numMountains,
            foregroundRangeCount,
            priceMountainRange,
            availableTreeStyles,
            archStyle
        ) {
            const landDepth = p.map(
                zIndex,
                0,
                p.max(FOREGROUND_LAND_ROWS - 1, 1),
                0,
                1
            );
            const baseY =
                p.height *
                p.lerp(p.random(0.86, 0.91), p.random(0.75, 0.82), landDepth);
            const rollAmplitude =
                p.height *
                p.lerp(
                    p.random(0.025, 0.045),
                    p.random(0.045, 0.075),
                    landDepth
                );
            const topY = baseY - rollAmplitude * 2;

            super(0, topY, p.width, p.height - topY);

            this.zIndex = zIndex;
            this.fillColor = getFlatLandFill(zIndex);
            this.baseY = baseY;
            this.rollAmplitude = rollAmplitude;
            this.sineAmplitude = rollAmplitude * p.random(0.15, 0.35);
            this.slope = p.height * p.random(-0.045, 0.045);
            this.waveCount = p.random(1.1, 2.6);
            this.wavePhase = p.random(Math.PI * 2);
            this.noiseStart = zIndex * 1000 + p.random(0, 1000);
            this.noiseSpan = p.random(1.8, 3.5);
            this.surfaceStep = PIXEL_ART_ENABLED ? PIXEL_VERTEX_GRID : 6;
            this.priceMountainRange = priceMountainRange;
            this.availableTreeStyles = availableTreeStyles;
            this.archStyle = archStyle;
            this.priceMountainGap = p.map(
                zIndex,
                0,
                p.max(foregroundRangeCount - 1, 1),
                p.height * 0.08,
                p.height * 0.04
            );
            this.featureScale = p.map(
                zIndex,
                0,
                p.max(FOREGROUND_LAND_ROWS - 1, 1),
                1,
                0.72
            );

            const landHue = p.hue(this.fillColor);
            const landSaturation = p.saturation(this.fillColor);
            const landBrightness = p.brightness(this.fillColor);
            const contrastBrightness =
                landBrightness < 78
                    ? p.min(landBrightness + p.random(48, 70), 155)
                    : p.max(landBrightness - p.random(44, 62), 18);
            const treeHue = (landHue + p.random(isNight ? 26 : 52, 82)) % 255;
            const rockHue = (landHue + p.random(-14, 18) + 255) % 255;

            const waterHue = (p.hue(bottomColor) + p.random(14, 42)) % 255;
            this.waterColor = p.color(
                waterHue,
                p.min(p.saturation(bottomColor) * 0.75 + 28, 255),
                p.min(p.brightness(bottomColor) * 1.18, 255),
                190
            );
            this.waterHighlightColor = p.color(
                waterHue,
                p.saturation(bottomColor) * 0.25,
                p.min(p.brightness(bottomColor) * 1.45, 255),
                145
            );
            this.shoreColor = p.color(
                p.hue(this.fillColor),
                p.saturation(this.fillColor) * 0.55,
                p.min(p.brightness(this.fillColor) * 1.28, 255),
                160
            );
            this.treeTrunkColor = p.color(
                rockHue,
                p.min(landSaturation * 1.15 + 26, 255),
                p.max(contrastBrightness * 0.45, 12),
                235
            );
            this.treeShadowColor = p.color(
                treeHue,
                p.min(landSaturation * 1.2 + 40, 255),
                p.max(contrastBrightness * 0.62, 18),
                230
            );
            this.treeFoliageColor = p.color(
                treeHue,
                p.min(landSaturation * 1.1 + 58, 255),
                contrastBrightness,
                235
            );
            this.rockDarkColor = p.color(
                rockHue,
                p.min(landSaturation * 0.9 + 28, 255),
                p.max(contrastBrightness * 0.68, 16),
                205
            );
            this.rockLightColor = p.color(
                rockHue,
                p.min(landSaturation * 0.45 + 18, 255),
                p.min(contrastBrightness * 1.24, 180),
                185
            );
            this.buildingWallColor = p.color(
                rockHue,
                p.min(landSaturation * 0.38 + 12, 255),
                p.min(contrastBrightness * 1.12, 215),
                230
            );
            this.buildingRoofColor = p.color(
                (rockHue + p.random(18, 48)) % 255,
                p.min(landSaturation * 0.82 + 22, 255),
                p.max(contrastBrightness * 0.48, 16),
                235
            );
            this.buildingDarkColor = p.color(
                rockHue,
                p.min(landSaturation * 0.6 + 18, 255),
                p.max(contrastBrightness * 0.28, 10),
                240
            );
            this.castleStoneColor = p.color(
                rockHue,
                p.min(landSaturation * 0.28 + 8, 255),
                p.min(contrastBrightness * 1.08, 210),
                230
            );
            this.castleShadowColor = p.color(
                rockHue,
                p.min(landSaturation * 0.5 + 15, 255),
                p.max(contrastBrightness * 0.42, 12),
                225
            );
            this.features = this.generateFeatures();
            this.flowerClusters = this.generateFlowerClusters();
            this.pathData = p.random() < 0.18 ? this.generatePath() : null;
        }

        getYAtX(x) {
            const amount = p.constrain(x / p.width, 0, 1);
            const noiseX = p.lerp(
                this.noiseStart,
                this.noiseStart + this.noiseSpan,
                amount
            );
            const roll = (p.noise(noiseX) - 0.5) * this.rollAmplitude * 2;
            const wave =
                Math.sin(amount * Math.PI * this.waveCount + this.wavePhase) *
                this.sineAmplitude;
            const slope = (amount - 0.5) * this.slope;
            const landY = this.baseY + roll + wave + slope;
            const priceFloor =
                this.priceMountainRange.getYAtX(x) + this.priceMountainGap;

            return p.max(snapToPixel(landY), priceFloor);
        }

        getFeatureY(x, depth = 0.5) {
            const surfaceY = this.getYAtX(x);
            return p.min(
                p.height - p.height * 0.03,
                surfaceY + (p.height - surfaceY) * depth
            );
        }

        generateFeatures() {
            const features = [];
            const hasLake = p.random() < LAND_LAKE_CHANCE;
            const hasRiver =
                p.random() < LAND_RIVER_CHANCE &&
                (!hasLake || p.random() < 0.3);

            if (hasLake) {
                features.push(this.createLake());
            }

            if (hasRiver) {
                features.push(this.createRiver());
            }

            if (p.random() < LAND_LANDMARK_CHANCE) {
                const minClusters = this.zIndex === 0 ? 16 : 12;
                const maxClusters = this.zIndex === 0 ? 30 : 22;
                const landmarkCount = Math.floor(
                    p.random(minClusters, maxClusters + 1)
                );

                for (let i = 0; i < landmarkCount; i += 1) {
                    const useTree =
                        this.availableTreeStyles.length > 0 &&
                        p.random() > 0.62;
                    features.push(
                        useTree
                            ? this.createPineCluster()
                            : this.createRockCluster()
                    );
                }
            }

            if (this.availableTreeStyles.length > 0) {
                if (p.random() < LAND_DENSE_FOREST_CHANCE) {
                    features.push(this.createDenseForest());
                } else if (p.random() < LAND_FOREST_CHANCE) {
                    const groveCount = Math.floor(
                        p.random(
                            this.zIndex === 0 ? 1 : 1,
                            this.zIndex === 0 ? 4 : 3
                        )
                    );

                    for (let i = 0; i < groveCount; i += 1) {
                        features.push(this.createPineGrove());
                    }
                }
            }

            if (p.random() < LAND_ROCK_FIELD_CHANCE) {
                features.push(this.createRockField());
            }

            const settlementRoll = p.random();
            if (settlementRoll < LAND_TOWN_CHANCE) {
                features.push(this.createTown());
            } else if (settlementRoll < LAND_TOWN_CHANCE + LAND_CASTLE_CHANCE) {
                features.push(this.createCastle());
            }

            const validFeatures = features.filter(Boolean);
            const waterFeatures = validFeatures.filter(
                (f) => f.type === 'lake' || f.type === 'river'
            );

            if (waterFeatures.length > 0) {
                validFeatures.forEach((feature) => {
                    if (feature.type !== 'pines') return;
                    feature.trees = feature.trees.filter(
                        (tree) =>
                            !waterFeatures.some((water) => {
                                if (water.type === 'lake') {
                                    const dx = tree.x - water.x;
                                    const dy = tree.y - water.y;
                                    return (
                                        (dx / (water.width * 0.58)) ** 2 +
                                            (dy / (water.height * 0.58)) ** 2 <
                                        1
                                    );
                                }
                                if (water.type === 'river') {
                                    return water.points.some((pt, i) => {
                                        if (i === water.points.length - 1)
                                            return false;
                                        const next = water.points[i + 1];
                                        const halfW =
                                            ((pt.width + next.width) * 0.5) / 2;
                                        const rdx = next.x - pt.x;
                                        const rdy = next.y - pt.y;
                                        const lenSq = rdx * rdx + rdy * rdy;
                                        if (lenSq === 0) return false;
                                        const t = p.constrain(
                                            ((tree.x - pt.x) * rdx +
                                                (tree.y - pt.y) * rdy) /
                                                lenSq,
                                            0,
                                            1
                                        );
                                        const cx = pt.x + t * rdx;
                                        const cy = pt.y + t * rdy;
                                        const ddx = tree.x - cx;
                                        const ddy = tree.y - cy;
                                        return (
                                            Math.sqrt(ddx * ddx + ddy * ddy) <
                                            halfW
                                        );
                                    });
                                }
                                return false;
                            })
                    );
                });
            }

            const settlements = validFeatures.filter(
                (f) => f.type === 'town' || f.type === 'castle'
            );

            if (settlements.length > 0) {
                const inSettlement = (x) =>
                    settlements.some((s) => {
                        const halfW = s.width * 0.55;
                        return x >= s.x - halfW && x <= s.x + halfW;
                    });

                return validFeatures.filter((feature) => {
                    if (
                        feature.type === 'town' ||
                        feature.type === 'castle' ||
                        feature.type === 'river'
                    )
                        return true;
                    if (feature.type === 'lake' || feature.type === 'rocks') {
                        return !inSettlement(feature.x);
                    }
                    if (feature.type === 'pines') {
                        feature.trees = feature.trees.filter(
                            (t) => !inSettlement(t.x)
                        );
                        return feature.trees.length > 0;
                    }
                    return true;
                });
            }

            return validFeatures;
        }

        createTown() {
            const archStyle = this.archStyle;
            const townWidth =
                p.width * p.random(0.18, 0.38) * this.featureScale;
            const centerX = p.random(
                townWidth * 0.5,
                p.width - townWidth * 0.5
            );
            const surfaceY = this.getYAtX(centerX);
            const buildingCount = Math.floor(p.random(5, 12));
            let maxHeight = 0;

            const buildings = Array.from({ length: buildingCount }, () => {
                const bx =
                    centerX + p.random(-townWidth * 0.45, townWidth * 0.45);
                const by = this.getYAtX(bx);
                const bw = p.width * p.random(0.028, 0.072) * this.featureScale;
                const bwh =
                    p.height * p.random(0.048, 0.105) * this.featureScale;
                const brh = bwh * p.random(0.3, 0.65);
                maxHeight = Math.max(maxHeight, bwh + brh);
                return {
                    x: bx,
                    y: by,
                    width: bw,
                    wallHeight: bwh,
                    roofHeight: brh,
                    style: p.random() < 0.72 ? 'gable' : 'flat',
                    chimneyOffset:
                        p.random() < 0.45 ? p.random(-0.28, 0.28) : null,
                    windowCount: Math.floor(p.random(1, 3)),
                };
            }).sort((a, b) => a.y - b.y);

            return {
                type: 'town',
                archStyle,
                x: centerX,
                y: surfaceY,
                width: townWidth,
                height: maxHeight,
                buildings,
            };
        }

        createCastle() {
            const archStyle = this.archStyle;
            const castleWidth =
                p.width * p.random(0.14, 0.28) * this.featureScale;
            const centerX = p.random(
                castleWidth * 0.5,
                p.width - castleWidth * 0.5
            );
            const surfaceY = this.getYAtX(centerX);
            const wallH = p.height * p.random(0.055, 0.095) * this.featureScale;
            const keepH = wallH * p.random(1.8, 2.8);
            const keepW = castleWidth * p.random(0.18, 0.26);
            const towerW = castleWidth * p.random(0.12, 0.18);
            const towerH = wallH * p.random(1.3, 1.9);
            const merlonW = towerW * 0.28;
            const merlonH = wallH * 0.18;

            return {
                type: 'castle',
                archStyle,
                x: centerX,
                y: surfaceY,
                width: castleWidth,
                height: keepH,
                wallH,
                keepH,
                keepW,
                towerW,
                towerH,
                merlonW,
                merlonH,
            };
        }

        createLake() {
            const width = p.width * p.random(0.05, 0.44) * this.featureScale;
            const height = p.height * p.random(0.014, 0.11) * this.featureScale;
            const x = p.random(width * 0.7, p.width - width * 0.7);
            const y = this.getFeatureY(x, p.random(0.38, 0.76));
            const pointCount = Math.floor(p.random(11, 16));
            const points = Array.from({ length: pointCount }, (_, index) => {
                const angle = p.map(index, 0, pointCount, 0, Math.PI * 2);
                return {
                    angle,
                    radius: p.random(0.78, 1.16),
                };
            });

            return { type: 'lake', x, y, width, height, points };
        }

        createRiver() {
            if (p.random() < 0.4) {
                return this.createHorizontalRiver();
            }
            return this.createVerticalRiver();
        }

        createHorizontalRiver() {
            const pointCount = Math.floor(p.random(6, 10));
            const riverThickness =
                p.width * p.random(0.018, 0.048) * this.featureScale;
            const midX = p.width * 0.5;
            let y = this.getYAtX(midX) + p.height * p.random(0.06, 0.42);
            const points = [];

            for (let i = 0; i < pointCount; i += 1) {
                const amount = i / (pointCount - 1);
                const x = p.lerp(-20, p.width + 20, amount);
                if (i > 0) {
                    y = p.constrain(
                        y + p.random(-p.height * 0.04, p.height * 0.04),
                        this.getYAtX(x) + p.height * 0.01,
                        p.height * 0.95
                    );
                }
                points.push({
                    x,
                    y,
                    width: riverThickness * p.random(0.88, 1.12),
                });
            }

            return { type: 'river', orientation: 'horizontal', points };
        }

        createVerticalRiver() {
            const startX = p.random(p.width * 0.15, p.width * 0.85);
            const startY = this.getYAtX(startX);
            const pointCount = Math.floor(p.random(5, 8));
            const points = [];
            let x = startX;

            for (let i = 0; i < pointCount; i += 1) {
                const amount = i / (pointCount - 1);
                x = p.constrain(
                    x + p.random(-p.width * 0.08, p.width * 0.08),
                    p.width * 0.05,
                    p.width * 0.95
                );
                points.push({
                    x,
                    y: p.lerp(startY, p.height + p.height * 0.05, amount),
                    width:
                        p.width *
                        p.lerp(0.014, 0.055, amount) *
                        this.featureScale,
                });
            }

            return { type: 'river', orientation: 'vertical', points };
        }

        createRockCluster() {
            const x = p.random(-p.width * 0.05, p.width * 1.05);
            const y = this.getFeatureY(x, p.random(0.2, 0.88));
            const count = Math.floor(p.random(3, 10));
            const rocks = Array.from({ length: count }, () => ({
                ox:
                    p.random(-p.width * 0.032, p.width * 0.032) *
                    this.featureScale,
                oy:
                    p.random(-p.height * 0.014, p.height * 0.018) *
                    this.featureScale,
                width: p.width * p.random(0.004, 0.048) * this.featureScale,
                height: p.height * p.random(0.004, 0.054) * this.featureScale,
            }));

            return { type: 'rocks', x, y, rocks };
        }

        createRockField() {
            const width = p.width * p.random(0.18, 0.42);
            const centerX = p.random(-width * 0.3, p.width + width * 0.3);
            const count = Math.floor(
                p.random(
                    this.zIndex === 0 ? 18 : 12,
                    this.zIndex === 0 ? 42 : 28
                )
            );
            const rocks = Array.from({ length: count }, () => {
                const x =
                    centerX +
                    p.random(-width * 0.5, width * 0.5) * this.featureScale;
                const y = this.getFeatureY(x, p.random(0.18, 0.94));

                return {
                    ox: x - centerX,
                    oy: y - this.getFeatureY(centerX, 0.56),
                    width: p.width * p.random(0.003, 0.038) * this.featureScale,
                    height:
                        p.height * p.random(0.003, 0.042) * this.featureScale,
                };
            });

            return {
                type: 'rocks',
                x: centerX,
                y: this.getFeatureY(centerX, 0.56),
                rocks,
            };
        }

        pickTreeStyle() {
            return this.availableTreeStyles[
                Math.floor(p.random(this.availableTreeStyles.length))
            ];
        }

        createPineCluster() {
            const x = p.random(-p.width * 0.05, p.width * 1.05);
            const count = Math.floor(p.random(5, 14));
            const style = this.pickTreeStyle();
            const trees = Array.from({ length: count }, () => {
                const tx =
                    x +
                    p.random(-p.width * 0.025, p.width * 0.025) *
                        this.featureScale;
                return {
                    x: tx,
                    y: this.getFeatureY(tx, p.random(0.02, 0.58)),
                    width: p.width * p.random(0.007, 0.017) * this.featureScale,
                    height:
                        p.height * p.random(0.03, 0.082) * this.featureScale,
                };
            }).sort((a, b) => a.y - b.y);

            return { type: 'pines', style, trees };
        }

        createPineGrove() {
            const groveWidth = p.width * p.random(0.18, 0.5);
            const centerX = p.random(
                -groveWidth * 0.3,
                p.width + groveWidth * 0.3
            );
            const treeCount = Math.floor(
                p.random(
                    this.zIndex === 0 ? 32 : 22,
                    this.zIndex === 0 ? 78 : 54
                )
            );
            const style = this.pickTreeStyle();
            const trees = Array.from({ length: treeCount }, () => {
                const tx =
                    centerX +
                    p.random(-groveWidth * 0.5, groveWidth * 0.5) *
                        this.featureScale;
                const depth = p.random(0.04, 0.92);
                const nearScale = p.lerp(0.72, 1.2, depth);

                return {
                    x: tx,
                    y: this.getFeatureY(tx, depth),
                    width:
                        p.width *
                        p.random(0.0055, 0.014) *
                        this.featureScale *
                        nearScale,
                    height:
                        p.height *
                        p.random(0.025, 0.075) *
                        this.featureScale *
                        nearScale,
                };
            }).sort((a, b) => a.y - b.y);

            return { type: 'pines', style, trees };
        }

        createDenseForest() {
            const style = this.pickTreeStyle();
            const treeCount = Math.floor(p.random(220, 400));
            const trees = Array.from({ length: treeCount }, () => {
                const tx = p.random(-p.width * 0.06, p.width * 1.06);
                const depth = p.random(0.02, 0.96);
                const nearScale = p.lerp(0.72, 1.2, depth);
                return {
                    x: tx,
                    y: this.getFeatureY(tx, depth),
                    width:
                        p.width *
                        p.random(0.005, 0.015) *
                        this.featureScale *
                        nearScale,
                    height:
                        p.height *
                        p.random(0.025, 0.08) *
                        this.featureScale *
                        nearScale,
                };
            }).sort((a, b) => a.y - b.y);

            return { type: 'pines', style, trees };
        }

        drawLakeShape(lake, scale, fillColor) {
            p.fill(fillColor);
            p.noStroke();
            p.beginShape();
            lake.points.forEach(({ angle, radius }) => {
                pixelVertex(
                    lake.x +
                        Math.cos(angle) * lake.width * 0.5 * radius * scale,
                    lake.y +
                        Math.sin(angle) * lake.height * 0.5 * radius * scale
                );
            });
            p.endShape(p.CLOSE);
        }

        drawLake(lake) {
            this.drawLakeShape(lake, 1.16, this.shoreColor);
            this.drawLakeShape(lake, 1, this.waterColor);

            p.stroke(this.waterHighlightColor);
            p.strokeWeight(1);
            const highlightCount = Math.floor(p.random(2, 5));
            for (let i = 0; i < highlightCount; i += 1) {
                const y =
                    lake.y + p.random(-lake.height * 0.2, lake.height * 0.2);
                p.line(
                    snapToPixel(lake.x - lake.width * p.random(0.12, 0.36)),
                    snapToPixel(y),
                    snapToPixel(lake.x + lake.width * p.random(0.12, 0.36)),
                    snapToPixel(y)
                );
            }
        }

        drawRiverShape(river, widthPadding, fillColor) {
            p.fill(fillColor);
            p.noStroke();
            p.beginShape();
            if (river.orientation === 'horizontal') {
                river.points.forEach(({ x, y, width }) => {
                    pixelVertex(x, y - width * 0.5 - widthPadding);
                });
                [...river.points].reverse().forEach(({ x, y, width }) => {
                    pixelVertex(x, y + width * 0.5 + widthPadding);
                });
            } else {
                river.points.forEach(({ x, y, width }) => {
                    pixelVertex(x - width * 0.5 - widthPadding, y);
                });
                [...river.points].reverse().forEach(({ x, y, width }) => {
                    pixelVertex(x + width * 0.5 + widthPadding, y);
                });
            }
            p.endShape(p.CLOSE);
        }

        drawRiver(river) {
            this.drawRiverShape(
                river,
                p.width * 0.006 * this.featureScale,
                this.shoreColor
            );
            this.drawRiverShape(river, 0, this.waterColor);
        }

        drawRocks({ x, y, rocks }) {
            rocks.forEach((rock, index) => {
                p.fill(
                    index % 2 === 0 ? this.rockLightColor : this.rockDarkColor
                );
                p.noStroke();
                p.beginShape();
                pixelVertex(x + rock.ox - rock.width * 0.5, y + rock.oy);
                pixelVertex(
                    x + rock.ox - rock.width * 0.18,
                    y + rock.oy - rock.height
                );
                pixelVertex(
                    x + rock.ox + rock.width * 0.42,
                    y + rock.oy - rock.height * 0.75
                );
                pixelVertex(x + rock.ox + rock.width * 0.5, y + rock.oy);
                p.endShape(p.CLOSE);
            });
        }

        drawPines({ style, trees }) {
            trees.forEach((tree) => {
                if (style === 'round') this.drawRoundTree(tree);
                else if (style === 'bare') this.drawBareTree(tree);
                else this.drawPineTree(tree);
            });
        }

        drawPineTree(tree) {
            const { x, y, width: w, height: h } = tree;
            p.noStroke();
            p.fill(this.treeTrunkColor);
            p.rect(
                snapToPixel(x - w * 0.12),
                snapToPixel(y - h * 0.32),
                snapToPixel(w * 0.24),
                snapToPixel(h * 0.32)
            );

            p.fill(this.treeShadowColor);
            p.triangle(
                snapToPixel(x),
                snapToPixel(y - h),
                snapToPixel(x - w * 0.64),
                snapToPixel(y - h * 0.04),
                snapToPixel(x + w * 0.64),
                snapToPixel(y - h * 0.04)
            );

            p.fill(this.treeFoliageColor);
            for (let layer = 0; layer < 3; layer += 1) {
                const layerY = y - h * (0.25 + layer * 0.23);
                const layerW = w * (1.75 - layer * 0.35);
                p.triangle(
                    snapToPixel(x),
                    snapToPixel(layerY - h * 0.28),
                    snapToPixel(x - layerW * 0.5),
                    snapToPixel(layerY + h * 0.18),
                    snapToPixel(x + layerW * 0.5),
                    snapToPixel(layerY + h * 0.18)
                );
            }
        }

        drawRoundTree(tree) {
            const { x, y, width: w, height: h } = tree;
            const canopyCY = y - h * 0.62;
            const canopyW = w * 1.85;
            const canopyH = h * 0.72;
            p.noStroke();

            // Trunk
            p.fill(this.treeTrunkColor);
            p.rect(
                snapToPixel(x - w * 0.14),
                snapToPixel(y - h * 0.46),
                snapToPixel(w * 0.28),
                snapToPixel(h * 0.46)
            );

            // Shadow side of canopy
            p.fill(this.treeShadowColor);
            p.ellipse(
                snapToPixel(x + w * 0.14),
                snapToPixel(canopyCY + h * 0.05),
                snapToPixel(canopyW),
                snapToPixel(canopyH)
            );

            // Main canopy
            p.fill(this.treeFoliageColor);
            p.ellipse(
                snapToPixel(x - w * 0.08),
                snapToPixel(canopyCY),
                snapToPixel(canopyW),
                snapToPixel(canopyH)
            );
        }

        drawBareTree(tree) {
            const { x, y, width: w, height: h } = tree;
            p.noStroke();
            p.fill(this.treeTrunkColor);

            // Tapered trunk
            p.beginShape();
            pixelVertex(x - w * 0.18, y);
            pixelVertex(x - w * 0.09, y - h * 0.58);
            pixelVertex(x - w * 0.05, y - h);
            pixelVertex(x + w * 0.05, y - h);
            pixelVertex(x + w * 0.09, y - h * 0.58);
            pixelVertex(x + w * 0.18, y);
            p.endShape(p.CLOSE);

            // Left branch
            p.triangle(
                snapToPixel(x - w * 0.07),
                snapToPixel(y - h * 0.52),
                snapToPixel(x - w * 0.95),
                snapToPixel(y - h * 0.7),
                snapToPixel(x + w * 0.02),
                snapToPixel(y - h * 0.6)
            );

            // Right branch
            p.triangle(
                snapToPixel(x + w * 0.07),
                snapToPixel(y - h * 0.34),
                snapToPixel(x + w * 0.9),
                snapToPixel(y - h * 0.55),
                snapToPixel(x - w * 0.02),
                snapToPixel(y - h * 0.42)
            );

            // Upper left twig
            p.triangle(
                snapToPixel(x - w * 0.05),
                snapToPixel(y - h * 0.72),
                snapToPixel(x - w * 0.55),
                snapToPixel(y - h * 0.84),
                snapToPixel(x + w * 0.02),
                snapToPixel(y - h * 0.78)
            );
        }

        drawGreekBuilding({ x, y, width: w, wallHeight: wh, windowCount }) {
            const pedH = wh * 0.32;
            p.noStroke();
            p.fill(this.buildingWallColor);
            p.rect(
                snapToPixel(x - w * 0.48),
                snapToPixel(y - wh * 0.94),
                snapToPixel(w * 0.96),
                snapToPixel(wh * 0.94)
            );
            p.rect(
                snapToPixel(x - w * 0.54),
                snapToPixel(y - wh * 0.06),
                snapToPixel(w * 1.08),
                snapToPixel(wh * 0.06)
            );
            p.rect(
                snapToPixel(x - w * 0.52),
                snapToPixel(y - wh),
                snapToPixel(w * 1.04),
                snapToPixel(wh * 0.1)
            );
            p.fill(this.buildingRoofColor);
            p.triangle(
                snapToPixel(x),
                snapToPixel(y - wh - pedH),
                snapToPixel(x - w * 0.56),
                snapToPixel(y - wh),
                snapToPixel(x + w * 0.56),
                snapToPixel(y - wh)
            );
            p.fill(this.buildingWallColor);
            const cols = Math.max(2, windowCount + 1);
            for (let i = 0; i < cols; i += 1) {
                const cx = x - w * 0.4 + (i / Math.max(cols - 1, 1)) * w * 0.8;
                p.rect(
                    snapToPixel(cx - w * 0.055),
                    snapToPixel(y - wh * 0.9),
                    snapToPixel(w * 0.11),
                    snapToPixel(wh * 0.84)
                );
            }
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - w * 0.11),
                snapToPixel(y - wh * 0.38),
                snapToPixel(w * 0.22),
                snapToPixel(wh * 0.38)
            );
        }

        drawArabicBuilding({
            x,
            y,
            width: w,
            wallHeight: wh,
            chimneyOffset,
            windowCount,
        }) {
            p.noStroke();
            p.fill(this.buildingWallColor);
            p.rect(
                snapToPixel(x - w * 0.5),
                snapToPixel(y - wh),
                snapToPixel(w),
                snapToPixel(wh)
            );
            p.fill(this.buildingRoofColor);
            p.rect(
                snapToPixel(x - w * 0.52),
                snapToPixel(y - wh - wh * 0.06),
                snapToPixel(w * 1.04),
                snapToPixel(wh * 0.06)
            );
            const domeR = w * 0.28;
            p.arc(
                snapToPixel(x),
                snapToPixel(y - wh - domeR * 0.05),
                snapToPixel(domeR * 2),
                snapToPixel(domeR * 1.8),
                Math.PI,
                Math.PI * 2,
                p.CHORD
            );
            if (chimneyOffset !== null) {
                const mx = x + w * chimneyOffset * 0.72;
                p.fill(this.buildingWallColor);
                p.rect(
                    snapToPixel(mx - w * 0.065),
                    snapToPixel(y - wh * 1.72),
                    snapToPixel(w * 0.13),
                    snapToPixel(wh * 0.92)
                );
                p.fill(this.buildingRoofColor);
                p.arc(
                    snapToPixel(mx),
                    snapToPixel(y - wh * 1.72),
                    snapToPixel(w * 0.18),
                    snapToPixel(w * 0.22),
                    Math.PI,
                    Math.PI * 2,
                    p.CHORD
                );
            }
            p.fill(this.buildingDarkColor);
            const dw = w * 0.26,
                dh = wh * 0.44;
            p.beginShape();
            pixelVertex(x - dw * 0.5, y);
            pixelVertex(x - dw * 0.5, y - dh * 0.58);
            pixelVertex(x, y - dh);
            pixelVertex(x + dw * 0.5, y - dh * 0.58);
            pixelVertex(x + dw * 0.5, y);
            p.endShape(p.CLOSE);
            for (let i = 0; i < windowCount; i += 1) {
                const wx =
                    windowCount === 1
                        ? x
                        : x + (i === 0 ? -w * 0.25 : w * 0.25);
                const ww = w * 0.16,
                    wh2 = wh * 0.22,
                    wy = y - wh * 0.64;
                p.beginShape();
                pixelVertex(wx - ww * 0.5, wy);
                pixelVertex(wx - ww * 0.5, wy - wh2 * 0.52);
                pixelVertex(wx, wy - wh2);
                pixelVertex(wx + ww * 0.5, wy - wh2 * 0.52);
                pixelVertex(wx + ww * 0.5, wy);
                p.endShape(p.CLOSE);
            }
        }

        drawEastAsianBuilding({
            x,
            y,
            width: w,
            wallHeight: wh,
            roofHeight: rh,
            windowCount,
        }) {
            const tiers = windowCount > 1 ? 2 : 1;
            p.noStroke();
            p.fill(this.buildingWallColor);
            p.rect(
                snapToPixel(x - w * 0.5),
                snapToPixel(y - wh),
                snapToPixel(w),
                snapToPixel(wh)
            );
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - w * 0.14),
                snapToPixel(y - wh * 0.4),
                snapToPixel(w * 0.28),
                snapToPixel(wh * 0.4)
            );
            for (let i = 0; i < windowCount; i += 1) {
                const wx =
                    windowCount === 1
                        ? x
                        : x + (i === 0 ? -w * 0.25 : w * 0.25);
                p.rect(
                    snapToPixel(wx - w * 0.09),
                    snapToPixel(y - wh * 0.72),
                    snapToPixel(w * 0.18),
                    snapToPixel(wh * 0.2)
                );
            }
            for (let t = 0; t < tiers; t += 1) {
                const baseY = y - wh - rh * t * 0.5;
                const tw = w * (1.5 - t * 0.3);
                p.fill(this.buildingRoofColor);
                p.beginShape();
                pixelVertex(x - tw * 0.62, baseY + rh * 0.1);
                pixelVertex(x - tw * 0.5, baseY);
                pixelVertex(x + tw * 0.5, baseY);
                pixelVertex(x + tw * 0.62, baseY + rh * 0.1);
                pixelVertex(x + tw * 0.1, baseY - rh * 0.46);
                pixelVertex(x - tw * 0.1, baseY - rh * 0.46);
                p.endShape(p.CLOSE);
                p.fill(this.buildingDarkColor);
                p.rect(
                    snapToPixel(x - tw * 0.5),
                    snapToPixel(baseY - rh * 0.09),
                    snapToPixel(tw),
                    snapToPixel(rh * 0.09)
                );
            }
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - w * 0.04),
                snapToPixel(y - wh - rh * tiers * 0.5 - rh * 0.18),
                snapToPixel(w * 0.08),
                snapToPixel(rh * 0.18)
            );
        }

        drawIndianBuilding({
            x,
            y,
            width: w,
            wallHeight: wh,
            roofHeight: rh,
            windowCount,
        }) {
            p.noStroke();
            p.fill(this.buildingWallColor);
            p.rect(
                snapToPixel(x - w * 0.5),
                snapToPixel(y - wh),
                snapToPixel(w),
                snapToPixel(wh)
            );
            p.fill(this.buildingDarkColor);
            const dw = w * 0.28,
                dh = wh * 0.44;
            p.beginShape();
            pixelVertex(x - dw * 0.5, y);
            pixelVertex(x - dw * 0.5, y - dh * 0.52);
            pixelVertex(x, y - dh);
            pixelVertex(x + dw * 0.5, y - dh * 0.52);
            pixelVertex(x + dw * 0.5, y);
            p.endShape(p.CLOSE);
            for (let i = 0; i < windowCount; i += 1) {
                const wx =
                    windowCount === 1
                        ? x
                        : x + (i === 0 ? -w * 0.25 : w * 0.25);
                p.rect(
                    snapToPixel(wx - w * 0.09),
                    snapToPixel(y - wh * 0.68),
                    snapToPixel(w * 0.18),
                    snapToPixel(wh * 0.22)
                );
            }
            const steps = 4;
            for (let s = 0; s < steps; s += 1) {
                const sw = w * (0.74 - s * 0.14);
                const sh = rh * 0.28;
                p.fill(
                    s % 2 === 0
                        ? this.buildingRoofColor
                        : this.buildingWallColor
                );
                p.rect(
                    snapToPixel(x - sw * 0.5),
                    snapToPixel(y - wh - s * sh - sh),
                    snapToPixel(sw),
                    snapToPixel(sh)
                );
            }
            p.fill(this.buildingRoofColor);
            p.ellipse(
                snapToPixel(x),
                snapToPixel(y - wh - steps * rh * 0.28 - rh * 0.14),
                snapToPixel(w * 0.22),
                snapToPixel(rh * 0.22)
            );
        }

        drawBattlements(left, right, topY, merlonW, merlonH) {
            const span = right - left;
            const count = Math.max(2, Math.floor(span / (merlonW * 2.4)));
            const spacing = span / count;
            for (let i = 0; i < count; i += 1) {
                const mx = left + spacing * (i + 0.2);
                p.rect(
                    snapToPixel(mx),
                    snapToPixel(topY - merlonH),
                    snapToPixel(merlonW),
                    snapToPixel(merlonH)
                );
            }
        }

        drawBuilding(b, archStyle = 'european') {
            if (archStyle === 'greek') {
                this.drawGreekBuilding(b);
                return;
            }
            if (archStyle === 'arabic') {
                this.drawArabicBuilding(b);
                return;
            }
            if (archStyle === 'east-asian') {
                this.drawEastAsianBuilding(b);
                return;
            }
            if (archStyle === 'indian') {
                this.drawIndianBuilding(b);
                return;
            }

            const {
                x,
                y,
                width: w,
                wallHeight: wh,
                roofHeight: rh,
                style,
                chimneyOffset,
                windowCount,
            } = b;
            p.noStroke();

            // Wall
            p.fill(this.buildingWallColor);
            p.rect(
                snapToPixel(x - w * 0.5),
                snapToPixel(y - wh),
                snapToPixel(w),
                snapToPixel(wh)
            );

            // Roof
            p.fill(this.buildingRoofColor);
            if (style === 'gable') {
                p.triangle(
                    snapToPixel(x),
                    snapToPixel(y - wh - rh),
                    snapToPixel(x - w * 0.6),
                    snapToPixel(y - wh),
                    snapToPixel(x + w * 0.6),
                    snapToPixel(y - wh)
                );
            } else {
                p.rect(
                    snapToPixel(x - w * 0.54),
                    snapToPixel(y - wh - rh * 0.55),
                    snapToPixel(w * 1.08),
                    snapToPixel(rh * 0.55)
                );
            }

            // Chimney
            if (chimneyOffset !== null) {
                p.fill(this.buildingDarkColor);
                const chW = w * 0.1;
                const chX = x + w * chimneyOffset;
                p.rect(
                    snapToPixel(chX - chW * 0.5),
                    snapToPixel(y - wh - rh * 0.72),
                    snapToPixel(chW),
                    snapToPixel(rh * 0.58)
                );
            }

            // Door
            p.fill(this.buildingDarkColor);
            const dw = w * 0.22,
                dh = wh * 0.36;
            p.rect(
                snapToPixel(x - dw * 0.5),
                snapToPixel(y - dh),
                snapToPixel(dw),
                snapToPixel(dh)
            );

            // Windows
            const winW = w * 0.18,
                winH = wh * 0.2,
                winY = y - wh * 0.6;
            for (let i = 0; i < windowCount; i += 1) {
                const wx =
                    windowCount === 1
                        ? x
                        : x + (i === 0 ? -w * 0.22 : w * 0.22);
                p.rect(
                    snapToPixel(wx - winW * 0.5),
                    snapToPixel(winY - winH),
                    snapToPixel(winW),
                    snapToPixel(winH)
                );
            }
        }

        drawTown({ buildings, archStyle }) {
            buildings.forEach((b) => this.drawBuilding(b, archStyle));
        }

        drawCastle(feature) {
            const { archStyle = 'european' } = feature;
            if (archStyle === 'arabic') {
                this.drawArabicFortress(feature);
                return;
            }
            if (archStyle === 'east-asian') {
                this.drawEastAsianCastle(feature);
                return;
            }
            if (archStyle === 'greek') {
                this.drawGreekFortress(feature);
                return;
            }
            if (archStyle === 'indian') {
                this.drawIndianFortress(feature);
                return;
            }
            this.drawEuropeanCastle(feature);
        }

        drawEuropeanCastle({
            x,
            y,
            width,
            wallH,
            keepH,
            keepW,
            towerW,
            towerH,
            merlonW,
            merlonH,
        }) {
            const left = x - width * 0.5;
            const right = x + width * 0.5;
            const ltX = left;
            const rtX = right - towerW;
            p.noStroke();

            // Curtain wall — shadow then face
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX + towerW * 0.85),
                snapToPixel(y - wallH + wallH * 0.08),
                snapToPixel(width - towerW * 1.7),
                snapToPixel(wallH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH)
            );
            this.drawBattlements(
                ltX + towerW,
                rtX,
                y - wallH,
                merlonW * 0.85,
                merlonH
            );

            // Gate opening
            const gw = (width - towerW * 2) * 0.22,
                gh = wallH * 0.72;
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - gw * 0.5),
                snapToPixel(y - gh),
                snapToPixel(gw),
                snapToPixel(gh)
            );

            // Left tower
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX + towerW * 0.1),
                snapToPixel(y - towerH + towerH * 0.06),
                snapToPixel(towerW),
                snapToPixel(towerH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX),
                snapToPixel(y - towerH),
                snapToPixel(towerW),
                snapToPixel(towerH)
            );
            this.drawBattlements(
                ltX,
                ltX + towerW,
                y - towerH,
                merlonW,
                merlonH
            );

            // Right tower
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(rtX + towerW * 0.1),
                snapToPixel(y - towerH + towerH * 0.06),
                snapToPixel(towerW),
                snapToPixel(towerH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(rtX),
                snapToPixel(y - towerH),
                snapToPixel(towerW),
                snapToPixel(towerH)
            );
            this.drawBattlements(
                rtX,
                rtX + towerW,
                y - towerH,
                merlonW,
                merlonH
            );

            // Keep
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(x - keepW * 0.5 + keepW * 0.08),
                snapToPixel(y - keepH + keepH * 0.05),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(x - keepW * 0.5),
                snapToPixel(y - keepH),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );

            // Keep windows
            p.fill(this.buildingDarkColor);
            const kww = keepW * 0.2,
                kwh = keepH * 0.1;
            for (let row = 0; row < 2; row += 1) {
                const wy = y - keepH * (0.35 + row * 0.28);
                for (let col = 0; col < 2; col += 1) {
                    const wx = x + (col === 0 ? -keepW * 0.22 : keepW * 0.22);
                    p.rect(
                        snapToPixel(wx - kww * 0.5),
                        snapToPixel(wy - kwh),
                        snapToPixel(kww),
                        snapToPixel(kwh)
                    );
                }
            }
            this.drawBattlements(
                x - keepW * 0.5,
                x + keepW * 0.5,
                y - keepH,
                merlonW * 0.75,
                merlonH
            );
        }

        drawArabicFortress({
            x,
            y,
            width,
            wallH,
            keepH,
            keepW,
            towerW,
            towerH,
        }) {
            const left = x - width * 0.5;
            const ltX = left;
            const rtX = left + width - towerW;
            p.noStroke();
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX + towerW * 0.9),
                snapToPixel(y - wallH + wallH * 0.08),
                snapToPixel(width - towerW * 1.8),
                snapToPixel(wallH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH)
            );
            const gw = (width - towerW * 2) * 0.26,
                gh = wallH * 0.9;
            p.fill(this.buildingDarkColor);
            p.beginShape();
            pixelVertex(x - gw * 0.5, y);
            pixelVertex(x - gw * 0.5, y - gh * 0.56);
            pixelVertex(x, y - gh);
            pixelVertex(x + gw * 0.5, y - gh * 0.56);
            pixelVertex(x + gw * 0.5, y);
            p.endShape(p.CLOSE);
            [ltX, rtX].forEach((tx) => {
                const tcx = tx + towerW * 0.5;
                p.fill(this.castleShadowColor);
                p.rect(
                    snapToPixel(tx + towerW * 0.1),
                    snapToPixel(y - towerH + towerH * 0.06),
                    snapToPixel(towerW),
                    snapToPixel(towerH)
                );
                p.fill(this.castleStoneColor);
                p.rect(
                    snapToPixel(tx),
                    snapToPixel(y - towerH),
                    snapToPixel(towerW),
                    snapToPixel(towerH)
                );
                p.fill(this.buildingRoofColor);
                p.arc(
                    snapToPixel(tcx),
                    snapToPixel(y - towerH - towerW * 0.22),
                    snapToPixel(towerW * 1.1),
                    snapToPixel(towerW * 1.4),
                    Math.PI,
                    Math.PI * 2,
                    p.CHORD
                );
            });
            const mW = keepW * 0.18,
                mH = keepH * 1.05;
            [-1, 1].forEach((side) => {
                const mx = x + side * keepW * 0.68;
                p.fill(this.castleStoneColor);
                p.rect(
                    snapToPixel(mx - mW * 0.5),
                    snapToPixel(y - mH),
                    snapToPixel(mW),
                    snapToPixel(mH)
                );
                p.fill(this.buildingRoofColor);
                p.arc(
                    snapToPixel(mx),
                    snapToPixel(y - mH - mW * 0.28),
                    snapToPixel(mW * 1.5),
                    snapToPixel(mW * 2),
                    Math.PI,
                    Math.PI * 2,
                    p.CHORD
                );
            });
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(x - keepW * 0.5 + keepW * 0.08),
                snapToPixel(y - keepH + keepH * 0.05),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(x - keepW * 0.5),
                snapToPixel(y - keepH),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            p.fill(this.buildingRoofColor);
            p.arc(
                snapToPixel(x),
                snapToPixel(y - keepH - keepW * 0.22),
                snapToPixel(keepW * 1.05),
                snapToPixel(keepH * 0.52),
                Math.PI,
                Math.PI * 2,
                p.CHORD
            );
            p.fill(this.buildingDarkColor);
            const kww = keepW * 0.2,
                kwh = keepH * 0.12;
            for (let row = 0; row < 2; row += 1) {
                for (let col = 0; col < 2; col += 1) {
                    const wx = x + (col === 0 ? -keepW * 0.22 : keepW * 0.22);
                    const wy = y - keepH * (0.3 + row * 0.26);
                    p.beginShape();
                    pixelVertex(wx - kww * 0.5, wy);
                    pixelVertex(wx - kww * 0.5, wy - kwh * 0.52);
                    pixelVertex(wx, wy - kwh);
                    pixelVertex(wx + kww * 0.5, wy - kwh * 0.52);
                    pixelVertex(wx + kww * 0.5, wy);
                    p.endShape(p.CLOSE);
                }
            }
        }

        _eastAsianRoof(cx, baseY, tw, rh) {
            p.beginShape();
            pixelVertex(cx - tw * 0.6, baseY + rh * 0.1);
            pixelVertex(cx - tw * 0.5, baseY);
            pixelVertex(cx + tw * 0.5, baseY);
            pixelVertex(cx + tw * 0.6, baseY + rh * 0.1);
            pixelVertex(cx + tw * 0.1, baseY - rh * 0.46);
            pixelVertex(cx - tw * 0.1, baseY - rh * 0.46);
            p.endShape(p.CLOSE);
        }

        drawEastAsianCastle({
            x,
            y,
            width,
            wallH,
            keepH,
            keepW,
            towerW,
            towerH,
        }) {
            const left = x - width * 0.5;
            const ltX = left;
            const rtX = left + width - towerW;
            p.noStroke();
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX),
                snapToPixel(y - wallH + wallH * 0.08),
                snapToPixel(width),
                snapToPixel(wallH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX),
                snapToPixel(y - wallH),
                snapToPixel(width),
                snapToPixel(wallH)
            );
            p.fill(this.buildingRoofColor);
            this._eastAsianRoof(x, y - wallH, width * 1.08, wallH * 0.56);
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - towerW * 0.52),
                snapToPixel(y - wallH * 0.84),
                snapToPixel(towerW * 1.04),
                snapToPixel(wallH * 0.84)
            );
            [ltX, rtX].forEach((tx) => {
                const tcx = tx + towerW * 0.5;
                for (let tier = 0; tier < 2; tier += 1) {
                    const tBaseY = y - towerH * 0.48 - tier * towerH * 0.4;
                    const tw = towerW * (1.28 - tier * 0.2);
                    p.fill(this.castleStoneColor);
                    p.rect(
                        snapToPixel(tcx - towerW * 0.5),
                        snapToPixel(tBaseY - towerH * 0.4),
                        snapToPixel(towerW),
                        snapToPixel(towerH * 0.4)
                    );
                    p.fill(this.buildingRoofColor);
                    this._eastAsianRoof(
                        tcx,
                        tBaseY - towerH * 0.4,
                        tw,
                        towerH * 0.26
                    );
                }
            });
            for (let tier = 0; tier < 3; tier += 1) {
                const kw = keepW * (1 - tier * 0.2);
                const kBaseY = y - keepH * (0.32 + tier * 0.26);
                p.fill(this.castleStoneColor);
                p.rect(
                    snapToPixel(x - kw * 0.5),
                    snapToPixel(kBaseY - keepH * 0.26),
                    snapToPixel(kw),
                    snapToPixel(keepH * 0.26)
                );
                p.fill(this.buildingRoofColor);
                this._eastAsianRoof(
                    x,
                    kBaseY - keepH * 0.26,
                    kw * 1.38,
                    keepH * 0.2
                );
            }
        }

        drawGreekFortress({
            x,
            y,
            width,
            wallH,
            keepH,
            keepW,
            towerW,
            towerH,
        }) {
            const left = x - width * 0.5;
            const ltX = left;
            const rtX = left + width - towerW;
            p.noStroke();
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX + towerW * 0.85),
                snapToPixel(y - wallH + wallH * 0.08),
                snapToPixel(width - towerW * 1.7),
                snapToPixel(wallH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH)
            );
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH - wallH * 0.11),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH * 0.11)
            );
            const gw = (width - towerW * 2) * 0.32;
            p.fill(this.buildingDarkColor);
            p.rect(
                snapToPixel(x - gw * 0.14),
                snapToPixel(y - wallH * 0.62),
                snapToPixel(gw * 0.28),
                snapToPixel(wallH * 0.62)
            );
            p.fill(this.buildingWallColor);
            for (let i = 0; i < 4; i += 1) {
                const cx = x - gw * 0.42 + (i / 3) * gw * 0.84;
                p.rect(
                    snapToPixel(cx - gw * 0.025),
                    snapToPixel(y - wallH * 0.88),
                    snapToPixel(gw * 0.05),
                    snapToPixel(wallH * 0.88)
                );
            }
            p.fill(this.buildingRoofColor);
            p.triangle(
                snapToPixel(x),
                snapToPixel(y - wallH * 1.32),
                snapToPixel(x - gw * 0.56),
                snapToPixel(y - wallH),
                snapToPixel(x + gw * 0.56),
                snapToPixel(y - wallH)
            );
            [ltX, rtX].forEach((tx) => {
                const tcx = tx + towerW * 0.5;
                p.fill(this.castleShadowColor);
                p.rect(
                    snapToPixel(tx + towerW * 0.1),
                    snapToPixel(y - towerH + towerH * 0.06),
                    snapToPixel(towerW),
                    snapToPixel(towerH)
                );
                p.fill(this.castleStoneColor);
                p.rect(
                    snapToPixel(tx),
                    snapToPixel(y - towerH),
                    snapToPixel(towerW),
                    snapToPixel(towerH)
                );
                p.fill(this.buildingWallColor);
                for (let i = 0; i < 3; i += 1) {
                    const cx = tx + towerW * (0.16 + i * 0.34);
                    p.rect(
                        snapToPixel(cx - towerW * 0.07),
                        snapToPixel(y - towerH * 0.9),
                        snapToPixel(towerW * 0.14),
                        snapToPixel(towerH * 0.84)
                    );
                }
                p.fill(this.buildingRoofColor);
                p.triangle(
                    snapToPixel(tcx),
                    snapToPixel(y - towerH * 1.3),
                    snapToPixel(tx),
                    snapToPixel(y - towerH),
                    snapToPixel(tx + towerW),
                    snapToPixel(y - towerH)
                );
            });
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(x - keepW * 0.5 + keepW * 0.08),
                snapToPixel(y - keepH + keepH * 0.05),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(x - keepW * 0.5),
                snapToPixel(y - keepH),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            p.fill(this.buildingWallColor);
            for (let i = 0; i < 4; i += 1) {
                const cx = x - keepW * 0.36 + (i / 3) * keepW * 0.72;
                p.rect(
                    snapToPixel(cx - keepW * 0.045),
                    snapToPixel(y - keepH * 0.88),
                    snapToPixel(keepW * 0.09),
                    snapToPixel(keepH * 0.82)
                );
            }
            p.fill(this.buildingRoofColor);
            p.triangle(
                snapToPixel(x),
                snapToPixel(y - keepH * 1.34),
                snapToPixel(x - keepW * 0.56),
                snapToPixel(y - keepH),
                snapToPixel(x + keepW * 0.56),
                snapToPixel(y - keepH)
            );
        }

        drawIndianFortress({
            x,
            y,
            width,
            wallH,
            keepH,
            keepW,
            towerW,
            towerH,
        }) {
            const left = x - width * 0.5;
            const ltX = left;
            const rtX = left + width - towerW;
            p.noStroke();
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(ltX + towerW * 0.85),
                snapToPixel(y - wallH + wallH * 0.08),
                snapToPixel(width - towerW * 1.7),
                snapToPixel(wallH)
            );
            p.fill(this.castleStoneColor);
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH)
            );
            p.fill(this.buildingRoofColor);
            p.rect(
                snapToPixel(ltX + towerW),
                snapToPixel(y - wallH - wallH * 0.1),
                snapToPixel(width - towerW * 2),
                snapToPixel(wallH * 0.1)
            );
            const gw = (width - towerW * 2) * 0.26,
                gh = wallH;
            p.fill(this.buildingDarkColor);
            p.beginShape();
            pixelVertex(x - gw * 0.5, y);
            pixelVertex(x - gw * 0.5, y - gh * 0.54);
            pixelVertex(x, y - gh);
            pixelVertex(x + gw * 0.5, y - gh * 0.54);
            pixelVertex(x + gw * 0.5, y);
            p.endShape(p.CLOSE);
            [ltX, rtX].forEach((tx) => {
                const tcx = tx + towerW * 0.5;
                p.fill(this.castleShadowColor);
                p.rect(
                    snapToPixel(tx + towerW * 0.1),
                    snapToPixel(y - towerH + towerH * 0.06),
                    snapToPixel(towerW),
                    snapToPixel(towerH)
                );
                const steps = 4;
                for (let s = 0; s < steps; s += 1) {
                    const sw = towerW * (1 - s * 0.16);
                    const sh = towerH * 0.2;
                    const sy = y - towerH * 0.48 - s * towerH * 0.2;
                    p.fill(
                        s % 2 === 0
                            ? this.castleStoneColor
                            : this.castleShadowColor
                    );
                    p.rect(
                        snapToPixel(tcx - sw * 0.5),
                        snapToPixel(sy - sh),
                        snapToPixel(sw),
                        snapToPixel(sh)
                    );
                }
                p.fill(this.buildingRoofColor);
                p.ellipse(
                    snapToPixel(tcx),
                    snapToPixel(
                        y - towerH * 0.48 - 4 * towerH * 0.2 - towerH * 0.06
                    ),
                    snapToPixel(towerW * 0.4),
                    snapToPixel(towerH * 0.12)
                );
            });
            p.fill(this.castleShadowColor);
            p.rect(
                snapToPixel(x - keepW * 0.5 + keepW * 0.08),
                snapToPixel(y - keepH + keepH * 0.05),
                snapToPixel(keepW),
                snapToPixel(keepH)
            );
            const gopSteps = 6;
            for (let s = 0; s < gopSteps; s += 1) {
                const kw = keepW * (1 - s * 0.11);
                const kh = keepH * 0.17;
                const ky = y - keepH * 0.42 - s * keepH * 0.11;
                p.fill(
                    s % 2 === 0 ? this.castleStoneColor : this.castleShadowColor
                );
                p.rect(
                    snapToPixel(x - kw * 0.5),
                    snapToPixel(ky - kh),
                    snapToPixel(kw),
                    snapToPixel(kh)
                );
            }
            p.fill(this.buildingRoofColor);
            p.ellipse(
                snapToPixel(x),
                snapToPixel(
                    y - keepH * 0.42 - gopSteps * keepH * 0.11 - keepH * 0.08
                ),
                snapToPixel(keepW * 0.32),
                snapToPixel(keepH * 0.1)
            );
        }

        generateFlowerClusters() {
            if (p.random() > 0.6) return [];
            const clusters = [];
            const clusterCount = Math.floor(p.random(2, 8));
            for (let c = 0; c < clusterCount; c++) {
                const cx = p.random(-p.width * 0.04, p.width * 1.04);
                const count = Math.floor(p.random(6, 24));
                const hue = p.random(0, 255);
                const sat = p.random(150, 245);
                const bright = p.random(190, 255);
                const flowers = [];
                for (let i = 0; i < count; i++) {
                    const fx = cx + p.random(-p.width * 0.028, p.width * 0.028) * this.featureScale;
                    const fy = this.getFeatureY(fx, p.random(0.02, 0.55));
                    flowers.push({
                        x: fx,
                        y: fy,
                        r: p.height * p.random(0.003, 0.008) * this.featureScale,
                        hue,
                        sat,
                        bright,
                    });
                }
                clusters.push(flowers);
            }
            return clusters;
        }

        generatePath() {
            const pointCount = Math.floor(p.random(5, 9));
            const pathW = p.width * p.random(0.011, 0.022) * this.featureScale;
            let cy = this.getYAtX(0) + p.height * p.random(0.06, 0.38);
            const points = [];
            for (let i = 0; i < pointCount; i++) {
                const t = i / (pointCount - 1);
                const x = p.lerp(-20, p.width + 20, t);
                if (i > 0) {
                    cy = p.constrain(
                        cy + p.random(-p.height * 0.022, p.height * 0.022),
                        this.getYAtX(x) + p.height * 0.01,
                        p.height * 0.9
                    );
                }
                points.push({ x, y: cy, w: pathW * p.random(0.85, 1.18) });
            }
            return points;
        }

        drawGroundSurfaceDetail() {
            const ctx = p.drawingContext;
            ctx.save();
            // Highlight along the top surface edge
            ctx.lineWidth = 1.5;
            ctx.strokeStyle = 'rgba(255,255,255,0.11)';
            ctx.beginPath();
            let first = true;
            for (let x = -20; x <= p.width + 20; x += this.surfaceStep) {
                const y = this.getYAtX(x);
                if (first) { ctx.moveTo(x, y); first = false; }
                else ctx.lineTo(x, y);
            }
            ctx.stroke();
            // Subtle shadow just below the surface to add depth
            ctx.lineWidth = 2;
            ctx.strokeStyle = 'rgba(0,0,0,0.09)';
            ctx.beginPath();
            first = true;
            for (let x = -20; x <= p.width + 20; x += this.surfaceStep) {
                const y = this.getYAtX(x) + 2.5;
                if (first) { ctx.moveTo(x, y); first = false; }
                else ctx.lineTo(x, y);
            }
            ctx.stroke();
            ctx.restore();
        }

        drawGrassTufts() {
            const ctx = p.drawingContext;
            const lr = Math.round(p.red(this.treeFoliageColor));
            const lg = Math.round(p.green(this.treeFoliageColor));
            const lb = Math.round(p.blue(this.treeFoliageColor));
            const dr = Math.round(p.red(this.treeShadowColor));
            const dg = Math.round(p.green(this.treeShadowColor));
            const db = Math.round(p.blue(this.treeShadowColor));

            const step = Math.max(3, Math.round(7 - this.zIndex));
            const maxH = p.height * p.lerp(0.009, 0.018, this.zIndex === 0 ? 1 : 0.4) * this.featureScale;
            const seed = this.noiseStart;

            ctx.save();
            ctx.lineWidth = 0.8;

            ctx.strokeStyle = `rgba(${lr},${lg},${lb},0.48)`;
            ctx.beginPath();
            for (let x = -10; x <= p.width + 10; x += step) {
                if (Math.sin(x * 0.23 + seed) > 0.1) continue;
                const surfY = this.getYAtX(x);
                const h = maxH * (0.45 + Math.abs(Math.sin(x * 0.31 + seed)) * 0.55);
                const lean = Math.sin(x * 0.17 + seed) * maxH * 0.42;
                ctx.moveTo(x, surfY);
                ctx.lineTo(x + lean, surfY - h);
            }
            ctx.stroke();

            ctx.strokeStyle = `rgba(${dr},${dg},${db},0.58)`;
            ctx.beginPath();
            for (let x = -10; x <= p.width + 10; x += step) {
                if (Math.sin(x * 0.23 + seed) <= 0.1) continue;
                const surfY = this.getYAtX(x);
                const h = maxH * (0.45 + Math.abs(Math.sin(x * 0.31 + seed)) * 0.55);
                const lean = Math.sin(x * 0.17 + seed) * maxH * 0.42;
                ctx.moveTo(x, surfY);
                ctx.lineTo(x + lean, surfY - h);
            }
            ctx.stroke();

            ctx.restore();
        }

        drawFlowerClusters() {
            if (!this.flowerClusters.length) return;
            this.flowerClusters.forEach((flowers) => {
                flowers.forEach(({ x, y, r, hue, sat, bright }) => {
                    // Stem
                    p.stroke(this.treeTrunkColor);
                    p.strokeWeight(0.5);
                    p.line(snapToPixel(x), snapToPixel(y), snapToPixel(x), snapToPixel(y - r * 2.2));
                    p.noStroke();
                    // Shadow bloom
                    p.fill(hue, sat * 0.7, bright * 0.62, 155);
                    p.circle(snapToPixel(x + r * 0.35), snapToPixel(y - r * 1.9), snapToPixel(r * 1.7));
                    // Main bloom
                    p.fill(hue, sat, bright, 205);
                    p.circle(snapToPixel(x), snapToPixel(y - r * 2.2), snapToPixel(r * 1.7));
                });
            });
            p.noStroke();
        }

        drawPath() {
            if (!this.pathData) return;
            const ctx = p.drawingContext;

            // Derive a muted, slightly desaturated dirt tone from the land fill
            const brightness = p.brightness(this.fillColor);
            const factor = brightness < 100 ? 1.32 : 0.70;
            const dR = Math.min(Math.round(p.red(this.fillColor) * factor + 18), 255);
            const dG = Math.min(Math.round(p.green(this.fillColor) * factor + 14), 255);
            const dB = Math.min(Math.round(p.blue(this.fillColor) * factor + 8), 255);

            ctx.save();
            // Edge shadow
            ctx.fillStyle = `rgba(${Math.max(dR-16,0)},${Math.max(dG-16,0)},${Math.max(dB-16,0)},0.36)`;
            ctx.beginPath();
            this.pathData.forEach(({ x, y, w }, i) => {
                if (i === 0) ctx.moveTo(x, y - w * 0.62);
                else ctx.lineTo(x, y - w * 0.62);
            });
            [...this.pathData].reverse().forEach(({ x, y, w }) => ctx.lineTo(x, y + w * 0.62));
            ctx.closePath();
            ctx.fill();
            // Path surface
            ctx.fillStyle = `rgba(${dR},${dG},${dB},0.36)`;
            ctx.beginPath();
            this.pathData.forEach(({ x, y, w }, i) => {
                if (i === 0) ctx.moveTo(x, y - w * 0.5);
                else ctx.lineTo(x, y - w * 0.5);
            });
            [...this.pathData].reverse().forEach(({ x, y, w }) => ctx.lineTo(x, y + w * 0.5));
            ctx.closePath();
            ctx.fill();
            ctx.restore();
        }

        drawFeature(feature) {
            if (feature.type === 'lake') {
                this.drawLake(feature);
                return;
            }

            if (feature.type === 'river') {
                this.drawRiver(feature);
                return;
            }

            if (feature.type === 'rocks') {
                this.drawRocks(feature);
                return;
            }

            if (feature.type === 'pines') {
                this.drawPines(feature);
                return;
            }

            if (feature.type === 'town') {
                this.drawTown(feature);
                return;
            }

            if (feature.type === 'castle') {
                this.drawCastle(feature);
            }
        }

        draw() {
            p.fill(this.fillColor);
            p.noStroke();
            p.beginShape();
            pixelVertex(-20, p.height);

            for (let x = -20; x <= p.width + 20; x += this.surfaceStep) {
                pixelVertex(x, this.getYAtX(x));
            }

            pixelVertex(p.width + 20, p.height);
            p.endShape(p.CLOSE);

            this.drawGroundSurfaceDetail();
            this.drawPath();
            this.drawGrassTufts();
            this.drawFlowerClusters();
            this.features.forEach((feature) => this.drawFeature(feature));
        }
    }

    class MountainRange extends Shape {
        constructor(
            zIndex,
            numMountains,
            heightLimit,
            foregroundRangeCount,
            priceMountainRange
        ) {
            let maxMountainHeight =
                ((zIndex + 1) / numMountains) * (p.height - p.height * 0.4);
            maxMountainHeight += p.min(p.pow(zIndex, p.random(3.3, 4)), 100);

            const foregroundDepth = p.map(
                zIndex,
                0,
                p.max(foregroundRangeCount - 1, 1),
                0.35,
                0.9
            );
            maxMountainHeight = p.min(
                maxMountainHeight,
                heightLimit * foregroundDepth
            );

            super(0, p.height - maxMountainHeight, p.width, maxMountainHeight);

            this.fillColor = getMountainFill(zIndex, numMountains);
            this.jaggedness = p.random(5, 10);
            this.startNoise = zIndex * p.width + p.random(0, p.width / 2);
            this.endNoise = this.startNoise + this.jaggedness;
            this.priceMountainRange = priceMountainRange;
            this.priceMountainGap = p.map(
                zIndex,
                0,
                p.max(foregroundRangeCount - 1, 1),
                p.height * 0.12,
                p.height * 0.04
            );
        }

        getYAtX(x) {
            const nx = p.map(x, 0, p.width, this.startNoise, this.endNoise);
            const y = this.height * p.noise(nx);
            return p.max(
                snapToPixel(p.height - y),
                this.priceMountainRange.getYAtX(x) + this.priceMountainGap
            );
        }

        draw() {
            p.fill(this.fillColor);
            p.noStroke();
            p.beginShape();
            pixelVertex(-20, p.height);

            for (let x = 1; x < p.width + 20; x += 1) {
                const nx = p.map(x, 0, p.width, this.startNoise, this.endNoise);
                const y = this.height * p.noise(nx);
                const mountainY = snapToPixel(p.height - y);
                const priceMountainY = this.priceMountainRange.getYAtX(x);
                pixelVertex(
                    x,
                    p.max(mountainY, priceMountainY + this.priceMountainGap)
                );
            }

            pixelVertex(p.width + 21, p.height);
            p.endShape(p.CLOSE);
            drawMountainFaceTerrain(
                (x) => this.getYAtX(x),
                this.y,
                this.startNoise * 73
            );
        }
    }

    const createMountainRanges = () => {
        const prices = getClosePrices(chartData);
        const ranges = [];
        const foregroundRangeCount = MAX_MOUNTAIN_RANGES - 1;
        const priceMountainRange = new PriceMountainRange(
            MAX_MOUNTAIN_RANGES - 1,
            MAX_MOUNTAIN_RANGES,
            prices
        );

        const availableTreeStyles = [];
        if (p.random() < 0.72) availableTreeStyles.push('pine');
        if (p.random() < 0.48) availableTreeStyles.push('round');
        if (p.random() < 0.22) availableTreeStyles.push('bare');

        const archStyle = p.random(ARCH_STYLES);

        for (let i = 0; i < foregroundRangeCount; i += 1) {
            if (i < FOREGROUND_LAND_ROWS) {
                ranges.push(
                    new FlatLandRange(
                        i,
                        MAX_MOUNTAIN_RANGES,
                        foregroundRangeCount,
                        priceMountainRange,
                        availableTreeStyles,
                        archStyle
                    )
                );
                continue;
            }

            ranges.push(
                new MountainRange(
                    i,
                    MAX_MOUNTAIN_RANGES,
                    priceMountainRange.peakHeight,
                    foregroundRangeCount,
                    priceMountainRange
                )
            );
        }

        ranges.push(priceMountainRange);

        return ranges;
    };

    const createClouds = () => {
        if (p.random() > CLOUD_SPAWN_CHANCE) {
            return [];
        }

        const cloudCount = Math.floor(
            p.random(CLOUD_MIN_COUNT, CLOUD_MAX_COUNT + 1)
        );

        return Array.from({ length: cloudCount }, () => new Cloud());
    };

    const createFogParams = () => {
        if (p.random() > FOG_CHANCE) return null;

        // Two layers: a dense base band and a thinner upper wisp
        return {
            base: {
                centerY: p.height * p.random(0.6, 0.76),
                halfHeight: p.height * p.random(0.09, 0.17),
                alpha: p.random(0.22, 0.42),
            },
            wisp:
                p.random() > 0.45
                    ? {
                          centerY: p.height * p.random(0.44, 0.6),
                          halfHeight: p.height * p.random(0.04, 0.09),
                          alpha: p.random(0.08, 0.18),
                      }
                    : null,
        };
    };

    const drawFogLayer = ({ centerY, halfHeight, alpha }) => {
        const skyAmount = p.constrain(centerY / (p.height * 0.4), 0, 1);
        const baseColor = p.lerpColor(topColor, bottomColor, skyAmount);
        const fogColor = p.color(
            p.hue(baseColor),
            p.saturation(baseColor) * 0.15,
            p.min(p.brightness(baseColor) * 1.35, 255)
        );

        const r = Math.round(p.red(fogColor));
        const g = Math.round(p.green(fogColor));
        const b = Math.round(p.blue(fogColor));

        const ctx = p.drawingContext;
        const top = centerY - halfHeight;
        const bottom = centerY + halfHeight;
        const gradient = ctx.createLinearGradient(0, top, 0, bottom);
        gradient.addColorStop(0, `rgba(${r},${g},${b},0)`);
        gradient.addColorStop(0.38, `rgba(${r},${g},${b},${alpha})`);
        gradient.addColorStop(0.62, `rgba(${r},${g},${b},${alpha})`);
        gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);

        const oldFill = ctx.fillStyle;
        ctx.fillStyle = gradient;
        ctx.fillRect(0, top, p.width, bottom - top);
        ctx.fillStyle = oldFill;
    };

    const drawFog = () => {
        if (!fogParams) return;
        drawFogLayer(fogParams.base);
        if (fogParams.wisp) drawFogLayer(fogParams.wisp);
    };

    const createRainParams = () => {
        if (p.random() > RAIN_CHANCE) return null;

        const intensity = p.random(0.4, 1.0);
        const layerDefs = [
            { count: 55, len: p.random(6, 12), alpha: 0.08, lw: 0.5 },
            { count: 80, len: p.random(12, 20), alpha: 0.15, lw: 0.7 },
            { count: 45, len: p.random(18, 28), alpha: 0.24, lw: 0.9 },
        ];
        const drops = layerDefs.map(({ count, len, alpha, lw }) => {
            const arr = [];
            for (let i = 0; i < count; i++) {
                arr.push({
                    x: p.random(0, p.width),
                    y: p.random(0, p.height),
                    len,
                    alpha: alpha * intensity,
                    lw,
                });
            }
            return arr;
        });
        return { drops, intensity };
    };

    const drawRain = () => {
        if (!rainParams) return;

        const ctx = p.drawingContext;

        // Subtle haze overlay
        const hazeGrad = ctx.createLinearGradient(0, 0, 0, p.height);
        hazeGrad.addColorStop(
            0,
            `rgba(180,200,230,${rainParams.intensity * 0.04})`
        );
        hazeGrad.addColorStop(
            1,
            `rgba(140,170,210,${rainParams.intensity * 0.14})`
        );
        const oldFill = ctx.fillStyle;
        ctx.fillStyle = hazeGrad;
        ctx.fillRect(0, 0, p.width, p.height);
        ctx.fillStyle = oldFill;

        // Streaks — batched by layer
        const dx = Math.cos(Math.PI / 2 + 0.15);
        const dy = Math.sin(Math.PI / 2 + 0.15);
        const oldAlpha = ctx.globalAlpha;
        rainParams.drops.forEach((layer) => {
            const { alpha, lw } = layer[0];
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = 'rgba(180,210,240,1)';
            ctx.lineWidth = lw;
            ctx.beginPath();
            layer.forEach(({ x, y, len }) => {
                ctx.moveTo(x, y);
                ctx.lineTo(x + dx * len, y + dy * len);
            });
            ctx.stroke();
        });
        ctx.globalAlpha = oldAlpha;
    };

    p.setup = () => {
        const renderScale = getRenderScale();
        if (PIXEL_ART_ENABLED) {
            p.pixelDensity(1);
        }

        const canvas = p.createCanvas(
            Math.ceil(p.windowWidth / renderScale),
            Math.ceil(p.windowHeight / renderScale)
        );
        p.colorMode(p.HSB, 255);

        if (PIXEL_ART_ENABLED) {
            p.noSmooth();
        } else {
            p.smooth();
        }

        if (PIXEL_ART_ENABLED && canvas?.elt) {
            canvas.elt.style.width = `${p.windowWidth}px`;
            canvas.elt.style.height = `${p.windowHeight}px`;
            canvas.elt.style.imageRendering = 'pixelated';
        }

        p.drawingContext.imageSmoothingEnabled = !PIXEL_ART_ENABLED;

        isNight = p.random() > 0.5;
        topColor = isNight ? getRandomNightBGColor() : getRandomDayBGColor();
        bottomColor = p.color(
            p.hue(topColor) + (isNight ? p.random(-15, 15) : 0),
            p.min(p.saturation(topColor) * (isNight ? 0.85 : 0.9), 255),
            p.brightness(topColor) * (isNight ? 1.4 : 1.5)
        );
        stars = isNight ? new Stars() : null;
        sun = isNight ? new Moon() : new Sun();
        clouds = createClouds();
        mountainRanges = createMountainRanges();
        fogParams = createFogParams();
        rainParams = createRainParams();

        const toHex = (c) => {
            const r = Math.round(p.red(c)).toString(16).padStart(2, '0');
            const g = Math.round(p.green(c)).toString(16).padStart(2, '0');
            const b = Math.round(p.blue(c)).toString(16).padStart(2, '0');
            return `#${r}${g}${b}`;
        };

        const rawColors = [
            topColor,
            bottomColor,
            sun.fillColor,
            sun.moonColor,
            sun.shadowColor,
            sun.craterFloorColor,
            sun.craterRimColor,
        ];
        mountainRanges.forEach((range) => {
            if (range.fillColor) rawColors.push(range.fillColor);
            if (range.waterColor) rawColors.push(range.waterColor);
            if (range.shoreColor) rawColors.push(range.shoreColor);
            if (range.treeFoliageColor) rawColors.push(range.treeFoliageColor);
            if (range.treeShadowColor) rawColors.push(range.treeShadowColor);
            if (range.treeTrunkColor) rawColors.push(range.treeTrunkColor);
            if (range.rockDarkColor) rawColors.push(range.rockDarkColor);
            if (range.rockLightColor) rawColors.push(range.rockLightColor);
            if (range.buildingWallColor)
                rawColors.push(range.buildingWallColor);
            if (range.buildingRoofColor)
                rawColors.push(range.buildingRoofColor);
            if (range.buildingDarkColor)
                rawColors.push(range.buildingDarkColor);
            if (range.castleStoneColor) rawColors.push(range.castleStoneColor);
            if (range.castleShadowColor)
                rawColors.push(range.castleShadowColor);
        });

        const seen = new Set();
        p._generatedPalette = rawColors
            .filter(Boolean)
            .map(toHex)
            .filter((hex) => !seen.has(hex) && seen.add(hex));

        p.noLoop();
    };

    p.draw = () => {
        drawBackground(topColor, bottomColor);
        if (stars) stars.draw();
        sun.draw();
        clouds.forEach((cloud) => {
            if (!cloud.foreground) cloud.draw();
        });

        for (let i = mountainRanges.length - 1; i >= 0; i -= 1) {
            mountainRanges[i].draw();
        }

        clouds.forEach((cloud) => {
            if (cloud.foreground) cloud.draw();
        });
        drawFog();
        drawRain();
    };
};
